import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { useDispatch, useSelector } from "react-redux"
import {
    HiOutlineHeart, HiOutlineLockClosed, HiOutlineCamera, HiOutlinePencilSquare,
    HiOutlineCheck, HiOutlineXMark, HiOutlineArrowPath, HiOutlineEye, HiOutlineEyeSlash,
    HiOutlineSparkles, HiOutlineUser, HiOutlineCheckCircle, HiOutlinePlus, HiOutlineTrash,
    HiOutlineStar, HiOutlineArrowUpCircle, HiOutlineArrowDownCircle,
    HiOutlineGift,
} from "react-icons/hi2"
import API from "../../Common/API"
import { setUserDetails } from "../../Store/Auth/actions"
import dummy from "../../Assets/Images/dummyProfile.jpeg"
import { useLanguage } from "../../Common/LanguageContext"
import PaymentModal from "./PaymentModal"
import RasiKattam from "../../Components/RasiKattam"

// ── Rasi → Star mapping using ACTUAL DB star names (Sanskrit/English) ──
const ZODIAC_STARS_MAP = {
    'Mesham (Aries)':        ['Ashwini','Bharani','Krittika'],
    'Rishabam (Taurus)':     ['Krittika','Rohini','Mrigashirsha'],
    'Mithunam (Gemini)':     ['Mrigashirsha','Ardra','Punarvasu'],
    'Kadagam (Cancer)':      ['Punarvasu','Pushya','Ashlesha'],
    'Simmam (Leo)':          ['Magha','Purva Phalguni','Uttara Phalguni'],
    'Kanni (Virgo)':         ['Uttara Phalguni','Hasta','Chitra'],
    'Thulam (Libra)':        ['Chitra','Swati','Vishakha'],
    'Viruchigam (Scorpio)':  ['Vishakha','Anuradha','Jyeshtha'],
    'Dhanusu (Sagittarius)': ['Mula','Purva Ashadha','Uttara Ashadha'],
    'Magaram (Capricorn)':   ['Uttara Ashadha','Shravana','Dhanishta'],
    'Kumbam (Aquarius)':     ['Dhanishta','Shatabhisha','Purva Bhadrapada'],
    'Meenam (Pisces)':       ['Purva Bhadrapada','Uttara Bhadrapada','Revati'],
}

// Filter allStars to those belonging to the given zodiac text
const filterStarsByRasi = (zodiacText, starPool) => {
    if (!zodiacText || !starPool?.length) return []
    const names = ZODIAC_STARS_MAP[zodiacText] || []
    if (!names.length) return []
    return starPool.filter(s => names.some(n => s.text?.toLowerCase() === n.toLowerCase()))
}

// Default 4×4 horoscope grid (centre cells are null, outer 12 are empty arrays)
const DEFAULT_JATHAGAM = [
    [], [], [], [],
    [], null, null, [],
    [], null, null, [],
    [], [], [], [],
]

/* ─── constants ─────────────────────────────────────────────── */
const MAX_PHOTOS = 5

const MARITAL_OPTIONS = [
    { label: 'Never Married (முதல் திருமணம்)', value: 'Never Married' },
    { label: 'Divorced (விவாகரத்து)',            value: 'Divorced'      },
    { label: 'Widow (விதவை)',                    value: 'Widow'         },
    { label: 'Widower (விதவையர்)',               value: 'Widower'       },
    { label: 'Separated (பிரிந்தனர்)',           value: 'seperated'     },
]

const daysLeft = (expiry) => {
    if (!expiry) return null
    const ms = new Date(expiry) - new Date()
    return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)))
}

/* ─── client-side image compression ─────────────────────────── *
 * Resizes the image to fit within MAX_DIM × MAX_DIM and re-encodes
 * as JPEG at QUALITY. Keeps the payload per photo under ~200 KB so
 * we stay well within the backend's 250 KB-per-photo limit and the
 * server's 8 MB body limit. Also prevents hitting localStorage's
 * 5 MB cap when userDetails is persisted.
 * ─────────────────────────────────────────────────────────────── */
const MAX_DIM = 900
const QUALITY = 0.78

const compressImage = (dataUrl) =>
    new Promise((resolve, reject) => {
        const img = new Image()
        img.onload = () => {
            let { width, height } = img
            if (width > MAX_DIM || height > MAX_DIM) {
                if (width > height) { height = Math.round((height * MAX_DIM) / width); width = MAX_DIM }
                else                { width  = Math.round((width  * MAX_DIM) / height); height = MAX_DIM }
            }
            const canvas = document.createElement("canvas")
            canvas.width  = width
            canvas.height = height
            const ctx = canvas.getContext("2d")
            ctx.drawImage(img, 0, 0, width, height)
            resolve(canvas.toDataURL("image/jpeg", QUALITY))
        }
        img.onerror = reject
        img.src = dataUrl
    })

/* ─── helpers ──────────────────────────────────────────────── */
const formatName = (u) =>
    [u?.firstName, u?.lastName].filter(Boolean).join(" ") || u?.refId || "Member"

/* ── Star helpers ──────────────────────────────────────────── */
// Detect MongoDB ObjectId (24 hex chars) — these are IDs, not display names
const isObjectId = (s) => typeof s === 'string' && /^[0-9a-f]{24}$/i.test(s)

// All 27 Nakshatras with their exact DB _ids and Sanskrit/English names
const NAKSHATRA_STATIC = [
    { text: "Ashwini",           value: "379dbec1cff019a14a422bbf" },
    { text: "Bharani",           value: "1a659211d2197301ccde3a7d" },
    { text: "Krittika",          value: "7d2d9bb0ba11edff1a502c68" },
    { text: "Rohini",            value: "16520fa902bbdd37bd5e4eb0" },
    { text: "Mrigashirsha",      value: "82cb01d7c1ab567e70c9541b" },
    { text: "Ardra",             value: "819e71520ea7c27a06338a12" },
    { text: "Punarvasu",         value: "5d83f852a18cef1adce859f3" },
    { text: "Pushya",            value: "958dd956cdcaca0870112351" },
    { text: "Ashlesha",          value: "a207e2b00a251c245559275d" },
    { text: "Magha",             value: "80dc0e079e0c7f729fc6aada" },
    { text: "Purva Phalguni",    value: "5e5045c332953a230fc7609e" },
    { text: "Uttara Phalguni",   value: "fddfa3ebb017991e0c335408" },
    { text: "Hasta",             value: "89f06dcb4914e1535a033692" },
    { text: "Chitra",            value: "9157ff145737149e62952974" },
    { text: "Swati",             value: "edbe5c599f008d32be2e6c62" },
    { text: "Vishakha",          value: "69b9d2570601ac4e29ca9b3a" },
    { text: "Anuradha",          value: "f668c1dc82bd8bc2f8c0cc32" },
    { text: "Jyeshtha",          value: "019d14c5ed0b9ab27b189a58" },
    { text: "Mula",              value: "d4d90716007c88a47bc29721" },
    { text: "Purva Ashadha",     value: "4d121b5910588d59b4fd8b48" },
    { text: "Uttara Ashadha",    value: "82e9d26d56a56234b64583c1" },
    { text: "Shravana",          value: "39bcc548e2a1a36225c2363e" },
    { text: "Dhanishta",         value: "8004de554d515f0ef586a1c6" },
    { text: "Shatabhisha",       value: "88b24ef9207f25690650873a" },
    { text: "Purva Bhadrapada",  value: "0491032d0b02eaf858542ad4" },
    { text: "Uttara Bhadrapada", value: "f52a36a86f3b8133a3e444c2" },
    { text: "Revati",            value: "975ea4cfcaa4b6bd19fa0b5d" },
]

// Resolve a star value to a display name using the allStars lookup table
// allStars: [{ text: "Rohini", value: "64abc123..." }]
const resolveStarName = (val, allStars) => {
    if (!val) return ""
    if (isObjectId(val)) {
        // It's an ObjectId — find the matching star name
        const found = allStars.find(s => s.value === val)
        return found ? found.text : ""  // return "" if not found (don't show the ObjectId)
    }
    return val  // already a plain name
}

const getStar = (u, allStars = []) => {
    // Try plain-text fields first (root level)
    const rootName = u?.starName || u?.nakshatra || u?.nakshatraName
    if (rootName && !isObjectId(rootName)) return rootName

    // Try additionalInformation plain text fields
    const aiName = u?.additionalInformation?.starName || u?.additionalInformation?.nakshatraName
    if (aiName && !isObjectId(aiName)) return aiName

    // Try root-level star (might be name or ObjectId)
    const rootStar = u?.star
    if (rootStar) return resolveStarName(rootStar, allStars)

    // Last resort: additionalInformation.star (usually ObjectId)
    const aiStar = u?.additionalInformation?.star
    if (aiStar) return resolveStarName(aiStar, allStars)

    return ""
}

const getPhoto = (u) => {
    const imgs = u?.images?.length ? u.images : u?.previewUrls || []
    return imgs.find(i => i?.imageUrl)?.imageUrl || null
}

const ageFromDob = (dob) => {
    if (!dob) return null
    const d = new Date(dob)
    if (Number.isNaN(d.getTime())) return null
    return Math.floor((Date.now() - d.getTime()) / (365.25 * 24 * 60 * 60 * 1000))
}

/* ─── profile completeness ────────────────────────────────── */
// Keys only — labels/tips come from t() inside components
const PROFILE_FIELD_KEYS = [
    "photo","firstName","lastName","dateOfBirth","starName","religion","caste",
    "maritalStatus","height","educationalQualification","occupationDetail",
    "salary","native","city","expectation",
]
const TOTAL_FIELDS = PROFILE_FIELD_KEYS.length

/* safely coerce any value (string | array | number | null) → trimmed string */
const str = (v) => (Array.isArray(v) ? v.join(", ") : (v == null ? "" : String(v))).trim()

const computeProgress = (user, form) => {
    const imgs = Array.isArray(user?.images) ? user.images : (user?.previewUrls || [])
    const hasPhoto = imgs.some(i => i?.imageUrl)
    const vals = {
        photo:                    hasPhoto,
        firstName:                !!str(form.firstName),
        lastName:                 !!str(form.lastName),
        dateOfBirth:              !!form.dateOfBirth,
        starName:                 !!str(form.starName),
        religion:                 !!str(form.religion),
        caste:                    !!str(form.caste),
        maritalStatus:            !!str(form.maritalStatus),
        height:                   !!str(form.height),
        educationalQualification: !!str(form.educationalQualification),
        occupationDetail:         !!str(form.occupationDetail),
        salary:                   !!str(form.salary),
        native:                   !!str(form.native),
        city:                     !!str(form.city),
        expectation:              !!str(form.expectation),
    }
    const filled = Object.values(vals).filter(Boolean).length
    return { vals, filled, total: TOTAL_FIELDS, pct: Math.round((filled / TOTAL_FIELDS) * 100) }
}

/* ─── reusable atoms ──────────────────────────────────────── */
// Tiny translated "Missing" badge — needs useLanguage so defined as a component
const MissingBadge = () => {
    const { t } = useLanguage()
    return (
        <span className="rounded-full bg-amber-50 border border-amber-200 px-2 py-0.5 text-[10px] font-semibold text-amber-600">
            {t("missing")}
        </span>
    )
}

const MsgBanner = ({ msg }) => !msg ? null : (
    <div className={`rounded-xl border px-4 py-2.5 text-sm font-semibold
        ${msg.type === "ok"
            ? "border-[#cfe8d4] bg-[#f2fbf4] text-[#1a8a3a]"
            : "border-[#efcaca] bg-[#fff5f5] text-[#a03e3e]"}`}>
        {msg.text}
    </div>
)

// Readable label + optional input — large, accessible design for all users
const SELECT_STYLE = {minHeight:48,fontSize:15,padding:'10px 14px',borderRadius:12,border:'1.5px solid #E5E7EB',background:'#F9FAFB',color:'#1F2937',outline:'none',width:'100%',boxSizing:'border-box',appearance:'none',cursor:'pointer'}
const INPUT_STYLE  = {minHeight:48,fontSize:15,padding:'10px 14px',borderRadius:12,border:'1.5px solid #E5E7EB',background:'#F9FAFB',color:'#1F2937',outline:'none',width:'100%',boxSizing:'border-box'}
const FOCUS_STYLE  = { borderColor:'#C8920A', background:'#fff', boxShadow:'0 0 0 3px rgba(200,146,10,0.12)' }
const BLUR_STYLE   = { borderColor:'#E5E7EB', background:'#F9FAFB', boxShadow:'none' }

const Field = ({ label, value, editing, name, onChange, type = "text", placeholder, tip, filled, options }) => {
    const displayLabel = options ? (options.find(o => o.value === value)?.label || value || '—') : (value || '—')
    return (
        <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2 flex-wrap">
                <label style={{fontSize:13,fontWeight:700,color:'#374151'}}>{label}</label>
                {editing && tip && (
                    <span style={{fontSize:11,color:'#9CA3AF',fontWeight:400}}>({tip})</span>
                )}
                {!editing && filled === false && (
                    <MissingBadge />
                )}
            </div>
            {editing ? (
                options ? (
                    <select
                        name={name}
                        value={value ?? ""}
                        onChange={onChange}
                        style={SELECT_STYLE}
                        onFocus={e => Object.assign(e.target.style, FOCUS_STYLE)}
                        onBlur={e  => Object.assign(e.target.style, BLUR_STYLE)}
                    >
                        <option value="">{placeholder || `-- ${label} தேர்ந்தெடுக --`}</option>
                        {options.map(o => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                    </select>
                ) : (
                    <input
                        type={type}
                        name={name}
                        value={value ?? ""}
                        onChange={onChange}
                        placeholder={placeholder || label}
                        style={INPUT_STYLE}
                        onFocus={e => Object.assign(e.target.style, FOCUS_STYLE)}
                        onBlur={e  => Object.assign(e.target.style, BLUR_STYLE)}
                    />
                )
            ) : (
                <p style={{fontSize:15,fontWeight: value ? 600 : 400,color: value ? '#1F2937' : '#9CA3AF',fontStyle: value ? 'normal' : 'italic'}}>
                    {displayLabel}
                </p>
            )}
        </div>
    )
}

// Helper: resolve array of IDs → comma-joined label string
const resolveMulti = (ids = [], options = []) => {
    if (!ids || !ids.length) return '—'
    return ids.map(id => {
        const o = options.find(x => x.value === id || x._id === id)
        return o ? (o.text || o.label) : id
    }).filter(Boolean).join(', ') || '—'
}

// Inline multi-checkbox for the edit slide-over
const MultiCheckField = ({ label, name, value = [], onChange, options = [] }) => {
    const toggle = (id) => {
        const next = value.includes(id) ? value.filter(x => x !== id) : [...value, id]
        onChange({ target: { name, value: next } })
    }
    return (
        <div style={{display:'flex',flexDirection:'column',gap:8}}>
            <label style={{fontSize:13,fontWeight:700,color:'#374151'}}>{label}</label>
            <div style={{display:'flex',flexWrap:'wrap',gap:8,padding:'10px 12px',borderRadius:12,border:'1.5px solid #E5E7EB',background:'#F9FAFB',minHeight:48}}>
                {options.length === 0 && <span style={{fontSize:13,color:'#9CA3AF'}}>Loading…</span>}
                {options.map(o => {
                    const sel = value.includes(o.value)
                    return (
                        <button key={o.value} type="button" onClick={() => toggle(o.value)}
                            style={{display:'flex',alignItems:'center',gap:5,padding:'5px 12px',borderRadius:99,fontSize:13,fontWeight:600,border:`1.5px solid ${sel ? '#C8920A' : '#E5E7EB'}`,background:sel ? '#FDF0C8' : '#fff',color:sel ? '#92660A' : '#6B7280',cursor:'pointer',transition:'all .15s'}}>
                            {sel && <span>✓</span>}
                            {o.text || o.label}
                        </button>
                    )
                })}
            </div>
        </div>
    )
}

const PwInput = ({ label, name, value, onChange, show, onToggle }) => (
    <div className="flex flex-col gap-1.5">
        <label className="text-xs font-bold text-slate-600 uppercase tracking-[0.1em]">{label}</label>
        <div className="relative">
            <input
                type={show ? "text" : "password"}
                name={name}
                value={value}
                onChange={onChange}
                placeholder={label}
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 pr-10 text-sm text-slate-800 outline-none focus:border-primeColor focus:bg-white transition"
            />
            <button type="button" onClick={onToggle} tabIndex={-1}
                title={show ? "Hide password" : "Show password"}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                {show ? <HiOutlineEyeSlash className="h-4 w-4" /> : <HiOutlineEye className="h-4 w-4" />}
            </button>
        </div>
    </div>
)

/* ─── Progress bar component ──────────────────────────────── */
// Map from field key → translation keys for label and tip
const FIELD_TRANS = {
    photo:                    { l: "firstName",    tip: "firstNameTip"    }, // overridden below
    firstName:                { l: "firstName",    tip: "firstNameTip"    },
    lastName:                 { l: "lastName",     tip: "lastNameTip"     },
    dateOfBirth:              { l: "dateOfBirth",  tip: "dateOfBirthTip"  },
    starName:                 { l: "starNakshatra",tip: "starTip"         },
    religion:                 { l: "religion",     tip: null              },
    caste:                    { l: "caste",        tip: null              },
    maritalStatus:            { l: "maritalStatus",tip: null              },
    height:                   { l: "height",       tip: null              },
    educationalQualification: { l: "education",    tip: null              },
    occupationDetail:         { l: "occupation",   tip: null              },
    salary:                   { l: "salary",       tip: null              },
    native:                   { l: "nativePlace",  tip: null              },
    city:                     { l: "currentCity",  tip: null              },
    expectation:              { l: "expectations", tip: "expectationsTip" },
}

const ProfileProgress = ({ pct, filled, total, vals, editing, onEdit }) => {
    const { t } = useLanguage()
    const barColor = pct >= 80 ? "#1a8a3a" : pct >= 50 ? "#C8920A" : "#D97706"
    const missingKeys = PROFILE_FIELD_KEYS.filter(k => !vals[k])
    const doneKeys    = PROFILE_FIELD_KEYS.filter(k =>  vals[k])

    return (
        <div style={{borderRadius:20,border:'1.5px solid rgba(224,207,160,0.35)',background:'#fff',padding:20,display:'flex',flexDirection:'column',gap:16}}>
            {/* Header row */}
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:12,flexWrap:'wrap'}}>
                <div>
                    <p style={{fontSize:11,fontWeight:800,letterSpacing:'0.14em',textTransform:'uppercase',color:'#C8920A'}}>{t("profileCompleteness")}</p>
                    <p style={{marginTop:4,fontSize:15,fontWeight:700,color:'#0A1628'}}>
                        {filled} / {total} {t("fieldsFilled")}
                    </p>
                </div>
                <div style={{width:56,height:56,borderRadius:'50%',border:`3px solid ${barColor}`,display:'flex',alignItems:'center',justifyContent:'center',background:`${barColor}18`,flexShrink:0}}>
                    <span style={{fontSize:16,fontWeight:900,color:barColor}}>{pct}%</span>
                </div>
            </div>

            {/* Progress bar */}
            <div style={{height:10,borderRadius:99,background:'#F3F4F6',overflow:'hidden'}}>
                <motion.div
                    style={{height:'100%',borderRadius:99,background:`linear-gradient(90deg,${barColor},${barColor}cc)`}}
                    initial={{ width: 0 }}
                    animate={{ width: `${pct}%` }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                />
            </div>

            {/* Complete message */}
            {pct === 100 && (
                <div style={{display:'flex',alignItems:'center',gap:8,padding:'10px 14px',borderRadius:12,background:'#F0FDF4',border:'1px solid #BBF7D0'}}>
                    <HiOutlineCheckCircle style={{color:'#16A34A',fontSize:20,flexShrink:0}}/>
                    <span style={{fontSize:14,fontWeight:700,color:'#15803D'}}>{t("greatJob")}</span>
                </div>
            )}

            {/* Missing fields checklist */}
            {missingKeys.length > 0 && !editing && (
                <div>
                    <p style={{fontSize:12,fontWeight:700,color:'#D97706',marginBottom:8}}>
                        ⚠️ {t("fillIn")} ({missingKeys.length} {t("fieldsRemaining")})
                    </p>
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:6}}>
                        {missingKeys.map(k => {
                            const tr = FIELD_TRANS[k]
                            const isPhtoto = k === "photo"
                            return (
                                <button key={k} type="button"
                                    onClick={onEdit}
                                    style={{display:'flex',alignItems:'center',gap:8,padding:'8px 12px',borderRadius:10,border:'1.5px solid #FDE68A',background:'#FFFBEB',cursor:'pointer',textAlign:'left'}}>
                                    <span style={{fontSize:16}}>{isPhtoto ? "📷" : "✏️"}</span>
                                    <span style={{fontSize:13,fontWeight:600,color:'#92400E'}}>{tr ? t(tr.l) : k}</span>
                                </button>
                            )
                        })}
                    </div>
                </div>
            )}

            {/* Completed fields */}
            {doneKeys.length > 0 && (
                <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
                    {doneKeys.map(k => {
                        const tr = FIELD_TRANS[k]
                        return (
                            <span key={k} style={{display:'inline-flex',alignItems:'center',gap:4,padding:'4px 10px',borderRadius:99,background:'#F0FDF4',border:'1px solid #BBF7D0',fontSize:11,fontWeight:600,color:'#15803D'}}>
                                <HiOutlineCheck style={{fontSize:11}}/>
                                {tr ? t(tr.l) : k}
                            </span>
                        )
                    })}
                </div>
            )}
        </div>
    )
}

/* ════════════════════════════════════════════════════════════ */
const MemberPortal = () => {
    const dispatch      = useDispatch()
    const userDetails   = useSelector((s) => s.auth.userDetails)
    const allStars      = useSelector((s) => s.auth.allStars    || [])
    const zodiacSigns   = useSelector((s) => s.auth.zodiacSigns || [])
    const allFlaws      = useSelector((s) => s.auth.flaws       || [])
    const allDhisa      = useSelector((s) => s.auth.dhisa       || [])
    const allStatus     = useSelector((s) => s.auth.status      || [])
    const occupationOpts= useSelector((s) => s.auth.occupation  || [])
    const { t, lang } = useLanguage()

    /* tabs */
    const [activeTab, setActiveTab] = useState("profile")   // "profile" | "shortlist"

    /* profile edit */
    const [editing,    setEditing]    = useState(false)
    const [form,       setForm]       = useState({})
    const [saving,     setSaving]     = useState(false)
    const [profileMsg, setProfileMsg] = useState(null)

    /* photos — multi-upload up to MAX_PHOTOS */
    const [photoUploading, setPhotoUploading] = useState(false)   // index being uploaded, or false
    const [photoUploadIdx, setPhotoUploadIdx] = useState(null)    // which slot is uploading
    const fileRef = useRef(null)

    /* photo lightbox */
    const [lightboxIdx, setLightboxIdx] = useState(null)   // null = closed, number = index in currentImages

    /* change-password modal */
    const [pwOpen,  setPwOpen]  = useState(false)
    const [pwForm,  setPwForm]  = useState({ oldPassword: "", newPassword: "", confirmNewPassword: "" })
    const [pwSaving,setPwSaving]= useState(false)
    const [pwMsg,   setPwMsg]   = useState(null)
    const [showPw,  setShowPw]  = useState({ old: false, new: false, confirm: false })

    /* shortlist */
    const [shortlist,  setShortlist]  = useState([])
    const [slLoading,  setSlLoading]  = useState(false)
    const [slRemoving, setSlRemoving] = useState(null) // id being removed

    /* plan details */
    const [planInfo,   setPlanInfo]   = useState(null)   // { planName, planRate, planValidity, validityInDays }
    const [plans,      setPlans]      = useState([])     // all available plans (for upgrade list)
    const [payModal,   setPayModal]   = useState(null)   // plan object to pay for, or null

    /* "same as present address" checkbox in edit form */
    const [sameAsPresent, setSameAsPresent] = useState(false)

    /* filtered stars — derived directly from form.zodiacSign every render (no async) */
    const filteredStars = useMemo(() => {
        if (!form.zodiacSign) return []
        const zodiacId = form.zodiacSign
        const zodiacText = zodiacSigns.find(z => z.value === zodiacId)?.text
            || zodiacSigns.find(z => z.text === zodiacId)?.text
            || (ZODIAC_STARS_MAP[zodiacId] ? zodiacId : '')
        if (!zodiacText) return []
        const starPool = allStars.length > 0 ? allStars : NAKSHATRA_STATIC
        return filterStarsByRasi(zodiacText, starPool)
    }, [form.zodiacSign, zodiacSigns, allStars])

    /* ── seed form ─────────────────────────────────────────── */
    useEffect(() => {
        if (!userDetails?._id) return
        if (editing) return   // don't wipe in-progress edits
        const fi = userDetails.familyInformation || {}
        const ci = userDetails.contactInformation || {}
        const ai = userDetails.additionalInformation || {}
        setForm({
            // ── Personal ──────────────────────────────────────
            firstName:                userDetails.firstName || "",
            lastName:                 userDetails.lastName  || userDetails.surname || "",
            dateOfBirth:              userDetails.dateOfBirth || "",
            birthTime:                userDetails.birthTime || "",
            birthPlace:               userDetails.birthPlace || "",
            religion:                 userDetails.religion || "",
            caste:                    userDetails.caste || "",
            maritalStatus:            userDetails.maritalStatus || "",
            noOfChild:                userDetails.noOfChild || "",
            childDetails:             userDetails.childDetails || "",
            height:                   userDetails.height || userDetails.heightInFeet || "",
            aboutMe:                  userDetails.aboutMe || "",
            // ── Career ────────────────────────────────────────
            educationalQualification: userDetails.educationalQualification || userDetails.education || "",
            profession:               userDetails.profession || "",
            occupationDetail:         userDetails.occupationDetail || userDetails.occupation || "",
            occupationLocation:       userDetails.occupationLocation || "",
            salary:                   userDetails.salary || "",
            salaryMode:               userDetails.salaryMode || "",
            // ── Location ──────────────────────────────────────
            native:                   userDetails.native || "",
            city:                     ci.presentAddress?.city || userDetails.city || "",
            // ── Astrology ─────────────────────────────────────
            starName:                 getStar(userDetails, allStars),
            zodiacSign:               ai.zodiacSign || "",
            padham:                   ai.padham || "",
            flaws:                    ai.flaws || [],
            thisaiIruppu:             userDetails.thisaiIruppu || "",
            thisaiIruppuDate:         userDetails.thisaiIruppuDate || "",
            thisaiIruppuMonth:        userDetails.thisaiIruppuMonth || "",
            thisaiIruppuYear:         userDetails.thisaiIruppuYear || "",
            // ── Partner Preference ────────────────────────────
            expectation:              ai.expectation || userDetails.expectation || "",
            expectedOccupation:       ai.expectedOccupation || [],
            expectedSalary:           ai.expectedSalary || "",
            expectedStatus:           ai.expectedStatus || [],
            expectedStars:            ai.expectedStars || [],
            expectedZodiacSigns:      ai.expectedZodiacSigns || [],
            expectedEducation:        ai.expectedEducationalQualification || "",
            // ── Family ────────────────────────────────────────
            fatherFirstName:          fi.fatherFirstName || "",
            fatherSurname:            fi.fatherSurname   || "",
            isFatherAlive:            fi.isFatherAlive != null ? fi.isFatherAlive : "",
            fatherOccupation:         fi.fatherOccupation|| "",
            fatherNative:             fi.fatherNative    || "",
            motherFirstName:          fi.motherFirstName || "",
            motherSurname:            fi.motherSurname   || "",
            isMotherAlive:            fi.isMotherAlive != null ? fi.isMotherAlive : "",
            motherOccupation:         fi.motherOccupation|| "",
            motherNative:             fi.motherNative    || "",
            numberOfSibilings:        fi.numberOfSibilings ?? fi.numberOfSiblings ?? "",
            numberOfMaleSibilings:    fi.numberOfMaleSibilings  ?? fi.numberOfMaleSiblings  ?? "",
            numberOfFemaleSibilings:  fi.numberOfFemaleSibilings ?? fi.numberOfFemaleSiblings ?? "",
            marriedMaleSibilings:     fi.marriedMaleSiblings ?? fi.marriedMaleSibilings ?? "",
            marriedFemaleSibilings:   fi.marriedFemaleSiblings ?? fi.marriedFemaleSibilings ?? "",
            siblingsDetails:          fi.siblingsDetails || "",
            assetInfo:                fi.assetInfo || "",
            // ── Contact ───────────────────────────────────────
            email:                    ci.email || userDetails.email || "",
            whatsappNumber:           ci.whatsappNumber || "",
            presentCity:              ci.presentAddress?.city     || "",
            presentState:             ci.presentAddress?.state    || "",
            presentDistrict:          ci.presentAddress?.district || "",
            presentPinCode:           ci.presentAddress?.pinCode  || "",
            presentAddressLine1:      ci.presentAddress?.addressLine1 || "",
            presentAddressLine2:      ci.presentAddress?.addressLine2 || "",
            nativeCity:               ci.nativeAddress?.city         || "",
            nativeState:              ci.nativeAddress?.state        || "",
            nativeDistrict:           ci.nativeAddress?.district     || "",
            nativePinCode:            ci.nativeAddress?.pinCode      || "",
            nativeAddressLine1:       ci.nativeAddress?.addressLine1 || "",
            nativeAddressLine2:       ci.nativeAddress?.addressLine2 || "",
            // ── Jathagam charts ───────────────────────────────
            rasiChart:  (ai.rasiChart  && ai.rasiChart.length  === 16) ? ai.rasiChart  : JSON.parse(JSON.stringify(DEFAULT_JATHAGAM)),
            amsaChart:  (ai.amsaChart  && ai.amsaChart.length  === 16) ? ai.amsaChart  : JSON.parse(JSON.stringify(DEFAULT_JATHAGAM)),
        })
    // Re-run when the full profile arrives from MemberHomeRoot's background fetch
    // (same _id, but dateOfBirth / updatedAt change once the full record loads).
    }, [userDetails?._id, userDetails?.dateOfBirth, userDetails?.updatedAt]) // eslint-disable-line

    // (star filtering now handled by useMemo above — no useEffect needed)

    // When allStars loads late (async Redux), patch ONLY the starName field —
    // never reset the whole form while the user may be typing other fields.
    useEffect(() => {
        if (allStars.length === 0) return
        const resolved = getStar(userDetails, allStars)
        if (!resolved) return
        setForm(prev => {
            // Only update if starName is still a raw MongoDB ID or empty
            const cur = prev.starName || ''
            const looksLikeId = /^[0-9a-f]{24}$/i.test(cur)
            if (looksLikeId || cur === '') return { ...prev, starName: resolved }
            return prev
        })
    }, [allStars.length]) // eslint-disable-line

    /* ── Tamil transliteration ─────────────────────────────── */
    const [tamilDisplay, setTamilDisplay] = useState({
        firstName: '', lastName: '', surname: '',
        fatherFirst: '', fatherSurname: '', motherFirst: '', motherSurname: '',
    })

    useEffect(() => {
        if (lang !== 'ta' || !userDetails?._id) return
        const xlit = (text) =>
            text
                ? API.post('/common/translate', { text })
                    .then(r => r.data?.data?.translatedText || text)
                    .catch(() => text)
                : Promise.resolve('')

        const fi = userDetails.familyInformation || {}
        Promise.all([
            // Use saved tFirstName if admin already stored it, else transliterate
            (userDetails.tFirstName && userDetails.tFirstName !== userDetails.firstName)
                ? Promise.resolve(userDetails.tFirstName) : xlit(userDetails.firstName),
            xlit(userDetails.lastName),
            (userDetails.tSurname && userDetails.tSurname !== userDetails.surname)
                ? Promise.resolve(userDetails.tSurname) : xlit(userDetails.surname),
            xlit(fi.fatherFirstName),
            xlit(fi.fatherSurname),
            xlit(fi.motherFirstName),
            xlit(fi.motherSurname),
        ]).then(([firstName, lastName, surname, fatherFirst, fatherSurname, motherFirst, motherSurname]) => {
            setTamilDisplay({ firstName, lastName, surname, fatherFirst, fatherSurname, motherFirst, motherSurname })
        })
    }, [lang, userDetails?._id]) // eslint-disable-line

    // Helper: display name respecting Tamil mode
    const displayName = (u) => {
        if (lang === 'ta') {
            const first = tamilDisplay.firstName || u?.tFirstName || u?.firstName || ''
            const last  = tamilDisplay.lastName  || u?.tLastName  || u?.lastName  || ''
            return [first, last].filter(Boolean).join(' ') || u?.refId || 'Member'
        }
        return [u?.firstName, u?.lastName].filter(Boolean).join(' ') || u?.refId || 'Member'
    }

    /* ── lock body scroll while edit slide-over is open ──── */
    useEffect(() => {
        document.body.style.overflow = editing ? "hidden" : ""
        return () => { document.body.style.overflow = "" }
    }, [editing])

    /* ── shortlist fetch ───────────────────────────────────── */
    useEffect(() => {
        setSlLoading(true)
        API.get("/matches/getShortlist")
            .then(r => setShortlist(r.data?.data || []))
            .catch(() => {})
            .finally(() => setSlLoading(false))
    }, [])

    const removeFromShortlist = async (profileId) => {
        setSlRemoving(profileId)
        try {
            await API.post("/matches/removeShortlist", { userId: profileId })
            setShortlist(prev => prev.filter(p => p._id !== profileId))
        } catch { /* silent */ }
        finally { setSlRemoving(null) }
    }

    /* ── plan info fetch ────────────────────────────────────── */
    useEffect(() => {
        if (!userDetails?._id) return
        API.get(`/plans/getSubscribedPlanDetailsByUserId/${userDetails._id}`)
            .then(r => { const d = r.data?.data; if (d?.planId) setPlanInfo(d.planId) })
            .catch(() => {})
        API.get("/plans/getPlansList")
            .then(r => setPlans(r.data?.data || []))
            .catch(() => {})
    }, [userDetails?._id]) // eslint-disable-line

    /* ── payment success — activate plan in Redux + localStorage ── */
    const handlePaymentSuccess = (updatedUser) => {
        if (!updatedUser) return
        persistUser(updatedUser)
        // Refresh plan info badge
        if (updatedUser.planId) {
            API.get(`/plans/getSubscribedPlanDetailsByUserId/${updatedUser._id}`)
                .then(r => { const d = r.data?.data; if (d?.planId) setPlanInfo(d.planId) })
                .catch(() => {})
        }
    }

    const progress = computeProgress(userDetails, form)

    /* ── form handlers ─────────────────────────────────────── */
    const handleFormChange = useCallback((e) => {
        const { name, value } = e.target
        setForm(prev => ({ ...prev, [name]: value }))
    }, [])

    /* ── persist to Redux + localStorage ──────────────────────── *
     * Strip base64 images before writing to localStorage to avoid hitting
     * the browser's 5 MB storage cap. The in-memory Redux store (and the
     * backend) keep the full images. On next login the full record is
     * fetched fresh from the server.
     * ─────────────────────────────────────────────────────────── */
    const persistUser = (data) => {
        const merged = { ...userDetails, ...data }
        dispatch(setUserDetails(merged))
        // Store without images to stay under localStorage 5 MB limit
        const { images: _stripped, ...safe } = merged
        try { localStorage.setItem("userDetails", JSON.stringify(safe)) } catch { /* quota */ }
    }

    const saveProfile = async () => {
        setSaving(true)
        setProfileMsg(null)
        try {
            const {
                // additionalInformation fields
                expectation, zodiacSign, padham, flaws,
                expectedOccupation, expectedSalary, expectedStatus, expectedStars, expectedZodiacSigns, expectedEducation,
                // familyInformation fields
                fatherFirstName, fatherSurname, isFatherAlive, fatherOccupation, fatherNative,
                motherFirstName, motherSurname, isMotherAlive, motherOccupation, motherNative,
                numberOfSibilings, numberOfMaleSibilings, numberOfFemaleSibilings,
                marriedMaleSibilings, marriedFemaleSibilings, siblingsDetails, assetInfo,
                // contactInformation fields
                email, whatsappNumber,
                presentCity, presentState, presentDistrict, presentPinCode, presentAddressLine1, presentAddressLine2,
                nativeCity, nativeState, nativeDistrict, nativePinCode, nativeAddressLine1, nativeAddressLine2,
                // jathagam charts
                rasiChart, amsaChart,
                ...base
            } = form

            // ── Star: preserve the existing ObjectId reference, only write the text name ──
            const existingStarId = userDetails?.additionalInformation?.star
            const starIdToKeep = existingStarId && isObjectId(existingStarId)
                ? existingStarId
                : (allStars.find(s => s.text === base.starName)?.value || existingStarId)

            const payload = {
                ...base,
                starName: base.starName || undefined,
                thisaiIruppu:      base.thisaiIruppu || undefined,
                thisaiIruppuDate:  base.thisaiIruppuDate || undefined,
                thisaiIruppuMonth: base.thisaiIruppuMonth || undefined,
                thisaiIruppuYear:  base.thisaiIruppuYear || undefined,
                additionalInformation: {
                    ...(userDetails?.additionalInformation || {}),
                    expectation,
                    zodiacSign:   zodiacSign || undefined,
                    padham:       padham || undefined,
                    flaws:        flaws?.length ? flaws : undefined,
                    starName:     base.starName || undefined,
                    star:         starIdToKeep || base.starName || undefined,
                    rasiChart:    rasiChart || undefined,
                    amsaChart:    amsaChart || undefined,
                    expectedOccupation:              expectedOccupation?.length ? expectedOccupation : undefined,
                    expectedSalary:                  expectedSalary || undefined,
                    expectedStatus:                  expectedStatus?.length ? expectedStatus : undefined,
                    expectedStars:                   expectedStars?.length ? expectedStars : undefined,
                    expectedZodiacSigns:             expectedZodiacSigns?.length ? expectedZodiacSigns : undefined,
                    expectedEducationalQualification: expectedEducation || undefined,
                },
                familyInformation: {
                    ...(userDetails?.familyInformation || {}),
                    fatherFirstName, fatherSurname,
                    isFatherAlive: isFatherAlive === "" ? undefined : isFatherAlive,
                    fatherOccupation, fatherNative,
                    motherFirstName, motherSurname,
                    isMotherAlive: isMotherAlive === "" ? undefined : isMotherAlive,
                    motherOccupation, motherNative,
                    numberOfSibilings:       numberOfSibilings === "" ? undefined : Number(numberOfSibilings),
                    numberOfMaleSibilings:   numberOfMaleSibilings === "" ? undefined : Number(numberOfMaleSibilings),
                    numberOfFemaleSibilings: numberOfFemaleSibilings === "" ? undefined : Number(numberOfFemaleSibilings),
                    marriedMaleSiblings:     marriedMaleSibilings === "" ? undefined : Number(marriedMaleSibilings),
                    marriedFemaleSiblings:   marriedFemaleSibilings === "" ? undefined : Number(marriedFemaleSibilings),
                    siblingsDetails:         siblingsDetails || undefined,
                    assetInfo:               assetInfo || undefined,
                },
                contactInformation: {
                    ...(userDetails?.contactInformation || {}),
                    email: email || undefined,
                    whatsappNumber,
                    presentAddress: {
                        ...(userDetails?.contactInformation?.presentAddress || {}),
                        addressLine1: presentAddressLine1,
                        addressLine2: presentAddressLine2,
                        city:     presentCity,
                        state:    presentState,
                        district: presentDistrict,
                        pinCode:  presentPinCode,
                    },
                    nativeAddress: {
                        ...(userDetails?.contactInformation?.nativeAddress || {}),
                        addressLine1: nativeAddressLine1,
                        addressLine2: nativeAddressLine2,
                        city:         nativeCity,
                        state:        nativeState,
                        district:     nativeDistrict,
                        pinCode:      nativePinCode,
                    },
                },
            }

            const res = await API.post("/user/updateMyProfile", payload)
            if (res.data?.data) persistUser(res.data.data)
            setEditing(false)
            setProfileMsg({ type: "ok", text: "Profile saved successfully." })
        } catch (err) {
            setProfileMsg({ type: "err", text: err?.response?.data?.error || "Could not save profile." })
        } finally {
            setSaving(false)
        }
    }

    /* ── multi-photo handlers ──────────────────────────────────── */
    const currentImages = Array.isArray(userDetails?.images)
        ? userDetails.images.filter(i => i?.imageUrl)
        : []

    const handlePhotoAdd = (e) => {
        const file = e.target.files?.[0]
        // Reset input so re-selecting the same file fires onChange again
        e.target.value = ""
        if (!file) return
        if (currentImages.length >= MAX_PHOTOS) {
            setProfileMsg({ type: "err", text: t("photoLimitReached") }); return
        }
        setPhotoUploading(true)
        setPhotoUploadIdx("add")
        const reader = new FileReader()
        reader.onload = async (ev) => {
            try {
                const compressed = await compressImage(ev.target.result)
                const newImages = [
                    ...currentImages,
                    { imageUrl: compressed, _id: `p${Date.now()}` },
                ]
                const res = await API.post("/user/updateMyProfile", { images: newImages })
                if (res.data?.data) persistUser(res.data.data)
                setProfileMsg({ type: "ok", text: "Photo added." })
            } catch (err) {
                setProfileMsg({ type: "err", text: err?.response?.data?.error || t("photoTooLarge") })
            } finally {
                setPhotoUploading(false); setPhotoUploadIdx(null)
            }
        }
        reader.readAsDataURL(file)
    }

    const handlePhotoReplace = (idx, e) => {
        const file = e.target.files?.[0]
        e.target.value = ""
        if (!file) return
        setPhotoUploading(true)
        setPhotoUploadIdx(idx)
        const reader = new FileReader()
        reader.onload = async (ev) => {
            try {
                const compressed = await compressImage(ev.target.result)
                const newImages = currentImages.map((img, i) =>
                    i === idx ? { imageUrl: compressed, _id: img._id || `p${Date.now()}` } : img
                )
                const res = await API.post("/user/updateMyProfile", { images: newImages })
                if (res.data?.data) persistUser(res.data.data)
                setProfileMsg({ type: "ok", text: "Photo updated." })
            } catch (err) {
                setProfileMsg({ type: "err", text: err?.response?.data?.error || t("photoTooLarge") })
            } finally {
                setPhotoUploading(false); setPhotoUploadIdx(null)
            }
        }
        reader.readAsDataURL(file)
    }

    const handlePhotoRemove = async (idx) => {
        setPhotoUploading(true)
        setPhotoUploadIdx(idx)
        try {
            const newImages = currentImages.filter((_, i) => i !== idx)
            const res = await API.post("/user/updateMyProfile", { images: newImages })
            if (res.data?.data) persistUser(res.data.data)
            setProfileMsg({ type: "ok", text: "Photo removed." })
        } catch (err) {
            setProfileMsg({ type: "err", text: err?.response?.data?.error || "Could not remove photo." })
        } finally {
            setPhotoUploading(false); setPhotoUploadIdx(null)
        }
    }

    /* ── password handlers ─────────────────────────────────── */
    const handlePwChange = (e) => {
        const { name, value } = e.target
        setPwForm(prev => ({ ...prev, [name]: value }))
    }
    const openPwModal  = () => { setPwOpen(true); setPwMsg(null); setPwForm({ oldPassword: "", newPassword: "", confirmNewPassword: "" }) }
    const closePwModal = () => { setPwOpen(false); setPwMsg(null) }
    const changePassword = async (e) => {
        e.preventDefault()
        if (pwForm.newPassword !== pwForm.confirmNewPassword) {
            setPwMsg({ type: "err", text: "New passwords do not match." }); return
        }
        setPwSaving(true); setPwMsg(null)
        try {
            await API.post("/auth/changeMyPassword", pwForm)
            setPwMsg({ type: "ok", text: "Password changed successfully." })
            setTimeout(closePwModal, 1800)
        } catch (err) {
            setPwMsg({ type: "err", text: err?.response?.data?.error || "Could not change password." })
        } finally {
            setPwSaving(false)
        }
    }

    const age  = ageFromDob(userDetails?.dateOfBirth)
    const star = getStar(userDetails, allStars)

    const TABS = [
        { key: "profile",   label: t("myProfileTab"),                              Icon: HiOutlineUser   },
        { key: "shortlist", label: `${t("shortlistTab")} (${shortlist.length})`,   Icon: HiOutlineHeart  },
    ]

    return (
        <motion.div className="space-y-5">

            {/* ── Page header ── */}
            <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:12,flexWrap:'wrap'}}>
                <div>
                    <p style={{fontSize:11,fontWeight:800,letterSpacing:'0.18em',textTransform:'uppercase',color:'#C8920A'}}>{t("myAccount")}</p>
                    <h2 style={{marginTop:4,fontSize:24,fontWeight:800,color:'#0A1628',fontFamily:"'Playfair Display',serif"}}>{t("myProfilePage")}</h2>
                </div>
                <button
                    type="button"
                    onClick={openPwModal}
                    title={t("changePassword")}
                    style={{display:'inline-flex',alignItems:'center',gap:8,padding:'12px 20px',borderRadius:14,border:'1.5px solid rgba(10,22,40,0.15)',background:'#fff',color:'#0A1628',fontSize:14,fontWeight:700,cursor:'pointer',boxShadow:'0 2px 10px rgba(10,22,40,0.07)',minHeight:48}}
                >
                    <HiOutlineLockClosed style={{fontSize:16}}/>
                    {t("changePassword")}
                </button>
            </div>

            {/* ── Profile card (name + edit actions) ── */}
            <div style={{borderRadius:20,border:'1.5px solid rgba(224,207,160,0.35)',background:'#fff',overflow:'hidden'}}>
                {/* Banner */}
                <div style={{height:80,background:'linear-gradient(135deg,#0A1628 0%,#112240 60%,rgba(200,146,10,0.3) 100%)'}}/>
                <div style={{padding:'0 20px 20px'}}>
                    <div style={{display:'flex',alignItems:'flex-end',justifyContent:'space-between',gap:12,marginTop:-40,marginBottom:16,flexWrap:'wrap'}}>
                        {/* Avatar — large */}
                        <div style={{position:'relative'}}>
                            <img
                                src={currentImages[0]?.imageUrl || dummy}
                                alt={displayName(userDetails)}
                                onClick={() => currentImages.length > 0 && setLightboxIdx(0)}
                                style={{width:80,height:80,borderRadius:18,objectFit:'cover',border:'4px solid #fff',boxShadow:'0 4px 16px rgba(10,22,40,0.18)',cursor: currentImages.length > 0 ? 'zoom-in' : 'default'}}
                            />
                            {/* No photo prompt badge */}
                            {currentImages.length === 0 && (
                                <label style={{position:'absolute',bottom:-8,right:-8,display:'flex',alignItems:'center',gap:4,background:'#C8920A',color:'#fff',borderRadius:99,padding:'4px 10px',fontSize:11,fontWeight:800,cursor:'pointer',border:'2px solid #fff',whiteSpace:'nowrap'}}>
                                    📷 {t("addPhoto")}
                                    <input type="file" accept="image/*" className="hidden" onChange={handlePhotoAdd} disabled={photoUploading}/>
                                </label>
                            )}
                        </div>
                        {/* Edit Profile CTA — large, prominent */}
                        <button type="button" onClick={() => { setEditing(true); setProfileMsg(null) }}
                            style={{display:'inline-flex',alignItems:'center',gap:8,background:'linear-gradient(135deg,#0A1628,#112240)',color:'#F5D264',border:'none',borderRadius:14,padding:'12px 22px',fontSize:15,fontWeight:800,cursor:'pointer',boxShadow:'0 4px 16px rgba(10,22,40,0.2)',minHeight:48}}>
                            <HiOutlinePencilSquare style={{fontSize:18}}/>
                            {t("editProfile")}
                        </button>
                    </div>

                    <h3 style={{fontSize:22,fontWeight:800,color:'#0A1628',fontFamily:"'Playfair Display',serif"}}>{displayName(userDetails)}</h3>
                    <p style={{marginTop:4,fontSize:14,color:'#6B7280'}}>
                        {[userDetails?.refId && `${t("ref")} ${userDetails.refId}`, userDetails?.gender, age && `${age} ${t("yrs")}`, star]
                            .filter(Boolean).join(" · ")}
                    </p>

                    {/* Plan badge */}
                    {(() => {
                        const paid = userDetails?.isPaid
                        const expiry = userDetails?.planValidityDate
                        const expired = paid && expiry && new Date(expiry) < new Date()
                        if (expired) return (
                            <span style={{marginTop:10,display:'inline-flex',alignItems:'center',gap:6,borderRadius:99,border:'1px solid #FECACA',background:'#FEF2F2',padding:'6px 14px',fontSize:13,fontWeight:700,color:'#DC2626'}}>
                                <HiOutlineLockClosed style={{fontSize:14}}/>{t("planExpired")}
                            </span>
                        )
                        if (paid) return (
                            <span style={{marginTop:10,display:'inline-flex',alignItems:'center',gap:6,borderRadius:99,border:'1px solid #BBF7D0',background:'#F0FDF4',padding:'6px 14px',fontSize:13,fontWeight:700,color:'#16A34A'}}>
                                <HiOutlineCheckCircle style={{fontSize:14}}/>{planInfo?.planName || t("paidMember")}
                            </span>
                        )
                        return (
                            <span style={{marginTop:10,display:'inline-flex',alignItems:'center',gap:6,borderRadius:99,border:'1px solid #FDE68A',background:'#FFFBEB',padding:'6px 14px',fontSize:13,fontWeight:700,color:'#D97706'}}>
                                <HiOutlineStar style={{fontSize:14}}/>{t("freePlan")}
                            </span>
                        )
                    })()}

                    {profileMsg && <div style={{marginTop:12}}><MsgBanner msg={profileMsg} /></div>}
                </div>
            </div>

            {/* ── Plan Card (removed — Plans tab handles this) ── */}
            {false && (() => {
                const paid    = userDetails?.isPaid
                const expiry  = userDetails?.planValidityDate
                const expired = paid && expiry && new Date(expiry) < new Date()
                const left    = paid && !expired ? daysLeft(expiry) : null
                const total   = planInfo?.planValidity || planInfo?.validityInDays || null
                const pct     = (left != null && total) ? Math.round((left / total) * 100) : null
                const barColor = pct == null ? "" : pct > 50 ? "bg-[#1a8a3a]" : pct > 20 ? "bg-amber-500" : "bg-red-500"

                if (paid && !expired) {
                    // ── Active paid plan ──
                    return (
                        <div className="rounded-2xl border border-[#cfe8d4] bg-[#f2fbf4] p-5 space-y-4">
                            <div className="flex items-start justify-between gap-3 flex-wrap">
                                <div>
                                    <p className="text-xs font-bold uppercase tracking-[0.14em] text-[#1a8a3a]">{t("membershipPlan")}</p>
                                    <h3 className="mt-1 text-lg font-bold text-tColor">{planInfo?.planName || t("paidMember")}</h3>
                                    {planInfo?.planRate && (
                                        <p className="text-xs text-slate-500">₹{planInfo.planRate.toLocaleString("en-IN")}</p>
                                    )}
                                </div>
                                <HiOutlineCheckCircle className="h-8 w-8 text-[#1a8a3a] shrink-0" />
                            </div>

                            {expiry && (
                                <div className="space-y-1.5">
                                    <div className="flex items-center justify-between text-xs font-semibold">
                                        <span className="text-slate-600">
                                            {t("expiresOn")} {new Date(expiry).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                                        </span>
                                        {left != null && (
                                            <span className={`font-bold ${left <= 15 ? "text-red-600" : left <= 30 ? "text-amber-600" : "text-[#1a8a3a]"}`}>
                                                {left} {t("daysLeft")}
                                            </span>
                                        )}
                                    </div>
                                    {pct != null && (
                                        <div className="h-2.5 w-full overflow-hidden rounded-full bg-white/70">
                                            <motion.div
                                                className={`h-full rounded-full ${barColor} transition-all`}
                                                initial={{ width: 0 }}
                                                animate={{ width: `${pct}%` }}
                                                transition={{ duration: 0.7, ease: "easeOut" }}
                                            />
                                        </div>
                                    )}
                                    {left != null && total && (
                                        <p className="text-[11px] text-slate-400">{left} {t("daysOf")} {total} {t("days")}</p>
                                    )}
                                </div>
                            )}

                            <div className="flex gap-2 flex-wrap">
                                <button type="button"
                                    onClick={() => setPayModal(planInfo)}
                                    className="inline-flex items-center gap-1.5 rounded-xl bg-[#1a8a3a] px-4 py-2 text-xs font-bold text-white hover:bg-[#156b2e] transition">
                                    {t("renewPlan")}
                                </button>
                                <button type="button"
                                    onClick={() => setPayModal(null) || setPlans(p => p) || document.getElementById("plan-picker")?.scrollIntoView({ behavior:"smooth" })}
                                    className="inline-flex items-center gap-1.5 rounded-xl border border-[#cfe8d4] bg-white px-4 py-2 text-xs font-bold text-[#1a8a3a] hover:border-[#1a8a3a] transition">
                                    {t("upgradePlan")}
                                </button>
                            </div>
                        </div>
                    )
                }

                if (paid && expired) {
                    // ── Expired plan ──
                    return (
                        <div className="rounded-2xl border border-red-200 bg-red-50 p-5 space-y-4">
                            <div className="flex items-start justify-between gap-3 flex-wrap">
                                <div>
                                    <p className="text-xs font-bold uppercase tracking-[0.14em] text-red-600">{t("membershipPlan")}</p>
                                    <h3 className="mt-1 text-lg font-bold text-tColor">{planInfo?.planName || t("paidMember")}</h3>
                                </div>
                                <HiOutlineLockClosed className="h-7 w-7 text-red-500 shrink-0" />
                            </div>
                            <p className="text-sm text-red-700">
                                {t("expiredOn")} {new Date(expiry).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}. {t("planExpired")}
                            </p>
                            <button type="button"
                                onClick={() => setPayModal(planInfo)}
                                className="inline-flex items-center gap-1.5 rounded-xl bg-red-600 px-4 py-2 text-xs font-bold text-white hover:bg-red-700 transition">
                                {t("renewPlan")}
                            </button>
                        </div>
                    )
                }

                // ── Free plan ──
                return (
                    <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 space-y-4">
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                            <div>
                                <p className="text-xs font-bold uppercase tracking-[0.14em] text-amber-700">{t("membershipPlan")}</p>
                                <h3 className="mt-1 text-lg font-bold text-tColor">{t("freePlan")}</h3>
                            </div>
                            <HiOutlineStar className="h-7 w-7 text-amber-500 shrink-0" />
                        </div>
                        <p className="text-sm text-amber-800">{t("freePlanDesc")}</p>

                        {/* Feature comparison */}
                        <div className="grid sm:grid-cols-2 gap-3">
                            <div className="rounded-xl border border-amber-200 bg-white/60 p-3 space-y-1.5">
                                <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-amber-700">{t("freePlan")}</p>
                                {[t("freeFeature1"), t("freeFeature2"), t("freeFeature3")].map(f => (
                                    <p key={f} className="flex items-start gap-1.5 text-xs text-slate-600">
                                        <HiOutlineCheck className="h-3.5 w-3.5 shrink-0 mt-0.5 text-amber-600" />{f}
                                    </p>
                                ))}
                            </div>
                            <div className="rounded-xl border border-primeColor/30 bg-primeSoft/40 p-3 space-y-1.5">
                                <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-primeColor">{t("paidMember")}</p>
                                {[t("premiumFeature1"), t("premiumFeature2"), t("premiumFeature3"), t("premiumFeature4")].map(f => (
                                    <p key={f} className="flex items-start gap-1.5 text-xs text-slate-600">
                                        <HiOutlineCheckCircle className="h-3.5 w-3.5 shrink-0 mt-0.5 text-primeColor" />{f}
                                    </p>
                                ))}
                            </div>
                        </div>

                        {/* Available plans */}
                        {plans.filter(p => p.active !== false).length > 0 && (
                            <div id="plan-picker" className="space-y-2">
                                <p className="text-xs font-semibold text-amber-800">{t("choosePlan")}</p>
                                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                                    {plans.filter(p => p.active !== false).map(p => (
                                        <button key={p._id} type="button"
                                            onClick={() => setPayModal(p)}
                                            className="rounded-xl border-2 border-amber-200 bg-white p-3 text-center space-y-0.5 hover:border-primeColor hover:bg-primeSoft/20 hover:shadow-sm transition focus:outline-none focus:ring-2 focus:ring-primeColor"
                                        >
                                            <p className="text-xs font-bold text-tColor truncate">{p.planName}</p>
                                            <p className="text-base font-bold text-primeColor">₹{(p.planRate||0).toLocaleString("en-IN")}</p>
                                            <p className="text-[10px] text-slate-400">{p.planValidity || p.validityInDays || "—"} {t("days")}</p>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {plans.filter(p => p.active !== false).length === 0 && (
                            <button type="button"
                                onClick={() => setPayModal({ _id: "default", planName: "Premium", planRate: 999, planValidity: 30 })}
                                className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-primeColor py-2.5 text-sm font-bold text-white hover:bg-primeDark transition">
                                {t("upgradeNow")}
                            </button>
                        )}
                    </div>
                )
            })()}

            {/* ── Photo gallery ── */}
            <div style={{borderRadius:20,border:'1.5px solid rgba(224,207,160,0.35)',background:'#fff',padding:20}}>
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:14,flexWrap:'wrap',gap:8}}>
                    <div>
                        <p style={{fontSize:13,fontWeight:800,color:'#C8920A',letterSpacing:'0.08em',textTransform:'uppercase'}}>{t("photos")}</p>
                        <p style={{fontSize:13,color:'#6B7280',marginTop:2}}>{t("photosDesc")}</p>
                    </div>
                    <span style={{fontSize:13,fontWeight:700,padding:'4px 12px',borderRadius:99,
                        background: currentImages.length >= MAX_PHOTOS ? '#FFFBEB' : '#FDF0C8',
                        color: currentImages.length >= MAX_PHOTOS ? '#D97706' : '#A07008',
                        border: '1px solid rgba(200,146,10,0.3)'}}>
                        {currentImages.length} / {MAX_PHOTOS}
                    </span>
                </div>

                {/* 2 cols on mobile, 5 on desktop — bigger tiles */}
                <div style={{display:'grid',gridTemplateColumns:'repeat(2,1fr)',gap:10}} className="sm:grid-cols-5-custom">
                    <style>{`.sm\\:grid-cols-5-custom { grid-template-columns: repeat(2,1fr) } @media(min-width:640px){.sm\\:grid-cols-5-custom{grid-template-columns:repeat(5,1fr)}}`}</style>

                    {currentImages.map((img, idx) => (
                        <div key={img._id || idx} style={{display:'flex',flexDirection:'column',gap:6}}>
                            {/* Photo */}
                            <div style={{position:'relative',aspectRatio:'1',borderRadius:14,overflow:'hidden',border:'2px solid rgba(200,146,10,0.2)'}}>
                                <img
                                    src={img.imageUrl}
                                    alt={`Photo ${idx + 1}`}
                                    onClick={() => setLightboxIdx(idx)}
                                    style={{width:'100%',height:'100%',objectFit:'cover',cursor:'zoom-in'}}
                                />
                                {idx === 0 && (
                                    <span style={{position:'absolute',top:6,left:6,display:'flex',alignItems:'center',gap:3,background:'#C8920A',color:'#fff',borderRadius:99,padding:'3px 8px',fontSize:10,fontWeight:800}}>
                                        ⭐ {t("primaryPhoto")}
                                    </span>
                                )}
                                {photoUploading && photoUploadIdx === idx && (
                                    <div style={{position:'absolute',inset:0,display:'flex',alignItems:'center',justifyContent:'center',background:'rgba(10,22,40,0.55)',borderRadius:14}}>
                                        <HiOutlineArrowPath style={{fontSize:24,color:'#F5D264',animation:'spin 1s linear infinite'}}/>
                                    </div>
                                )}
                            </div>
                            {/* Always-visible action buttons below photo */}
                            <div style={{display:'flex',gap:4}}>
                                <label style={{flex:1,display:'flex',alignItems:'center',justifyContent:'center',gap:4,padding:'7px 4px',borderRadius:10,border:'1.5px solid #E5E7EB',background:'#F9FAFB',fontSize:11,fontWeight:700,color:'#374151',cursor:'pointer'}}>
                                    {photoUploading && photoUploadIdx === idx
                                        ? <HiOutlineArrowPath style={{fontSize:13,animation:'spin 1s linear infinite'}}/>
                                        : <HiOutlineCamera style={{fontSize:13}}/>}
                                    {t("profilePhotoTip") || "Change"}
                                    <input type="file" accept="image/*" className="hidden"
                                        onChange={(e) => handlePhotoReplace(idx, e)}
                                        disabled={photoUploading} />
                                </label>
                                <button type="button"
                                    onClick={() => handlePhotoRemove(idx)}
                                    disabled={photoUploading}
                                    style={{display:'flex',alignItems:'center',justifyContent:'center',padding:'7px 10px',borderRadius:10,border:'1.5px solid #FECACA',background:'#FEF2F2',color:'#DC2626',cursor:'pointer',fontSize:13}}>
                                    <HiOutlineTrash style={{fontSize:13}}/>
                                </button>
                            </div>
                        </div>
                    ))}

                    {/* Add photo slot — large & obvious */}
                    {currentImages.length < MAX_PHOTOS && (
                        <label style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:8,borderRadius:14,border:'2.5px dashed #C8920A',background:'#FFFBEB',cursor:'pointer',minHeight:120,padding:12}}>
                            {photoUploading && photoUploadIdx === "add"
                                ? <HiOutlineArrowPath style={{fontSize:28,color:'#C8920A',animation:'spin 1s linear infinite'}}/>
                                : <>
                                    <span style={{fontSize:28}}>📷</span>
                                    <span style={{fontSize:13,fontWeight:800,color:'#C8920A',textAlign:'center'}}>{t("addPhoto")}</span>
                                  </>
                            }
                            <input ref={fileRef} type="file" accept="image/*" className="hidden"
                                onChange={handlePhotoAdd}
                                disabled={photoUploading} />
                        </label>
                    )}
                </div>
            </div>

            {/* ── Progress bar ── */}
            <ProfileProgress
                pct={progress.pct}
                filled={progress.filled}
                total={progress.total}
                vals={progress.vals}
                editing={false}
                onEdit={() => { setEditing(true); setProfileMsg(null) }}
            />

            {/* ── Tabs ── */}
            <div style={{display:'flex',gap:8,borderBottom:'2px solid rgba(224,207,160,0.3)'}}>
                {TABS.map(({ key, label, Icon }) => {
                    const active = activeTab === key
                    return (
                        <button key={key} type="button" onClick={() => setActiveTab(key)}
                            style={{
                                position:'relative',display:'inline-flex',alignItems:'center',gap:8,
                                padding:'14px 20px',fontSize:15,fontWeight:800,border:'none',cursor:'pointer',
                                borderRadius:'12px 12px 0 0',transition:'all .2s',
                                background: active ? 'rgba(200,146,10,0.12)' : 'transparent',
                                color: active ? '#C8920A' : '#6B7280',
                                borderBottom: active ? '3px solid #C8920A' : '3px solid transparent',
                                minHeight:52,
                            }}>
                            <Icon style={{fontSize:18}}/>
                            {label}
                            {active && (
                                <motion.div layoutId="portal-tab-line"
                                    style={{position:'absolute',bottom:-2,left:8,right:8,height:3,borderRadius:99,background:'#C8920A'}} />
                            )}
                        </button>
                    )
                })}
            </div>

            {/* ── PROFILE TAB ── */}
            {activeTab === "profile" && (
                <div style={{borderRadius:20,border:'1.5px solid rgba(224,207,160,0.35)',background:'#fff',padding:28,display:'flex',flexDirection:'column',gap:20}}>
                    {/* Section: Personal */}
                    <div>
                        <p style={{fontSize:13,fontWeight:800,color:'#C8920A',letterSpacing:'0.08em',textTransform:'uppercase',marginBottom:16,display:'flex',alignItems:'center',gap:8}}>👤 {t("personalDetails")}</p>
                        <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4">
                            <Field label={t("firstName")}      name="firstName"
                                value={lang === 'ta' ? (tamilDisplay.firstName || form.firstName) : form.firstName}
                                editing={false} onChange={handleFormChange}
                                tip={t("firstNameTip")} filled={progress.vals.firstName} />
                            <Field label={t("lastName")}       name="lastName"
                                value={lang === 'ta' ? (tamilDisplay.lastName || form.lastName) : form.lastName}
                                editing={false} onChange={handleFormChange}
                                tip={t("lastNameTip")} filled={progress.vals.lastName} />
                            <Field label={t("dateOfBirth")}    name="dateOfBirth"              value={form.dateOfBirth}              editing={false} onChange={handleFormChange}
                                type="date" tip={t("dateOfBirthTip")} filled={progress.vals.dateOfBirth} />
                            <Field label={t("starNakshatra")}  name="starName"                 value={form.starName}                 editing={false} onChange={handleFormChange}
                                placeholder={t("phStar")} tip={t("starTip")} filled={progress.vals.starName} />
                            <Field label={t("religion")}       name="religion"                 value={form.religion}                 editing={false} onChange={handleFormChange}
                                filled={progress.vals.religion} />
                            <Field label={t("caste")}          name="caste"                    value={form.caste}                    editing={false} onChange={handleFormChange}
                                filled={progress.vals.caste} />
                            <Field label={t("maritalStatus")}  name="maritalStatus"            value={form.maritalStatus}            editing={false} onChange={handleFormChange}
                                placeholder={t("phMarital")} filled={progress.vals.maritalStatus} options={MARITAL_OPTIONS} />
                            <Field label={t("height")}         name="height"                   value={form.height}                   editing={false} onChange={handleFormChange}
                                placeholder={t("phHeight")} filled={progress.vals.height} />
                            <Field label={"Birth Time"} name="birthTime" value={form.birthTime} editing={false} />
                            <Field label={"Birth Place"} name="birthPlace" value={form.birthPlace} editing={false} />
                            <Field label={"No. of Children"} name="noOfChild" value={form.noOfChild} editing={false} />
                            <Field label={"Child Details"} name="childDetails" value={form.childDetails} editing={false} />
                        </div>
                        {form.aboutMe && (
                            <div style={{marginTop:12}}>
                                <label style={{fontSize:13,fontWeight:700,color:'#374151'}}>About Me</label>
                                <p style={{marginTop:6,fontSize:15,color:'#374151',lineHeight:1.6}}>{form.aboutMe}</p>
                            </div>
                        )}
                    </div>

                    <hr style={{border:'none',borderTop:'1px solid rgba(224,207,160,0.3)'}}/>

                    {/* Section: Career */}
                    <div>
                        <p style={{fontSize:13,fontWeight:800,color:'#C8920A',letterSpacing:'0.08em',textTransform:'uppercase',marginBottom:16,display:'flex',alignItems:'center',gap:8}}>🎓 {t("educationCareer")}</p>
                        <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4">
                            <Field label={t("education")}      name="educationalQualification" value={form.educationalQualification} editing={false} onChange={handleFormChange}
                                placeholder={t("phEducation")} filled={progress.vals.educationalQualification} />
                            <Field label={"Profession"}       name="profession"               value={form.profession}               editing={false} />
                            <Field label={t("occupation")}     name="occupationDetail"         value={form.occupationDetail}         editing={false} onChange={handleFormChange}
                                placeholder={t("phOccupation")} filled={progress.vals.occupationDetail} />
                            <Field label={"Occupation Location"} name="occupationLocation"     value={form.occupationLocation}       editing={false} />
                            <Field label={t("salary")}         name="salary"                   value={form.salary}                   editing={false} onChange={handleFormChange}
                                placeholder={t("phSalary")} filled={progress.vals.salary} />
                            <Field label={"Salary Mode"}      name="salaryMode"               value={form.salaryMode}               editing={false} />
                            <Field label={t("nativePlace")}    name="native"                   value={form.native}                   editing={false} onChange={handleFormChange}
                                placeholder={t("phNative")} filled={progress.vals.native} />
                            <Field label={t("currentCity")}    name="city"                     value={form.city}                     editing={false} onChange={handleFormChange}
                                placeholder={t("phCity")} filled={progress.vals.city} />
                        </div>
                    </div>

                    <hr style={{border:'none',borderTop:'1px solid rgba(224,207,160,0.3)'}}/>

                    {/* Section: Astrology */}
                    <div>
                        <p style={{fontSize:13,fontWeight:800,color:'#C8920A',letterSpacing:'0.08em',textTransform:'uppercase',marginBottom:16,display:'flex',alignItems:'center',gap:8}}>🔯 கூடுதல் தகவல் (Astrology)</p>
                        <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4">
                            <div>
                                <label style={{fontSize:13,fontWeight:700,color:'#374151'}}>Zodiac Sign (ராசி)</label>
                                <p style={{marginTop:6,fontSize:15,fontWeight:600,color:'#1F2937'}}>
                                    {zodiacSigns.length > 0
                                        ? (zodiacSigns.find(z => z.value === form.zodiacSign || z._id === form.zodiacSign)?.text || form.zodiacSign || '—')
                                        : (form.zodiacSign || '—')}
                                </p>
                            </div>
                            <Field label={"Padham (பாதம்)"} name="padham" value={form.padham} editing={false} />
                            <div>
                                <label style={{fontSize:13,fontWeight:700,color:'#374151'}}>Flaws / Dhosam</label>
                                <p style={{marginTop:6,fontSize:15,color:'#374151'}}>{resolveMulti(form.flaws, allFlaws)}</p>
                            </div>
                            <div>
                                <label style={{fontSize:13,fontWeight:700,color:'#374151'}}>Thisai Iruppu (திசை இருப்பு)</label>
                                <p style={{marginTop:6,fontSize:15,color:'#374151'}}>
                                    {(() => {
                                        const dhisaName = allDhisa.length > 0
                                            ? (allDhisa.find(d => d.value === form.thisaiIruppu || d._id === form.thisaiIruppu)?.text || form.thisaiIruppu)
                                            : form.thisaiIruppu
                                        const parts = [dhisaName, form.thisaiIruppuDate, form.thisaiIruppuMonth, form.thisaiIruppuYear].filter(Boolean)
                                        return parts.length > 0 ? parts.join(' / ') : '—'
                                    })()}
                                </p>
                            </div>
                        </div>
                        {/* Jathagam Charts VIEW */}
                        {(form.rasiChart || form.amsaChart) && (
                            <div style={{marginTop:16}}>
                                <p style={{fontSize:12,fontWeight:800,color:'#6B7280',letterSpacing:'0.08em',textTransform:'uppercase',marginBottom:12}}>ஜாதகம் (Jathagam)</p>
                                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>
                                    <div>
                                        <p style={{fontSize:12,fontWeight:700,color:'#6B7280',marginBottom:8,textAlign:'center'}}>ராசி கட்டம் (Rasi)</p>
                                        <RasiKattam
                                            rasiValues={form.rasiChart}
                                            readOnly={true}
                                            kattamInnerInjectclass="min-h-[56px] text-[11px]"
                                        />
                                    </div>
                                    <div>
                                        <p style={{fontSize:12,fontWeight:700,color:'#6B7280',marginBottom:8,textAlign:'center'}}>அம்சம் கட்டம் (Amsa)</p>
                                        <RasiKattam
                                            rasiValues={form.amsaChart}
                                            readOnly={true}
                                            kattamInnerInjectclass="min-h-[56px] text-[11px]"
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <hr style={{border:'none',borderTop:'1px solid rgba(224,207,160,0.3)'}}/>

                    {/* Section: Partner Preference */}
                    <div>
                        <p style={{fontSize:13,fontWeight:800,color:'#C8920A',letterSpacing:'0.08em',textTransform:'uppercase',marginBottom:16,display:'flex',alignItems:'center',gap:8}}>💑 Partner Preference</p>
                        <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4">
                            <div>
                                <label style={{fontSize:13,fontWeight:700,color:'#374151'}}>Expected Occupation</label>
                                <p style={{marginTop:6,fontSize:15,color:'#374151'}}>{resolveMulti(form.expectedOccupation, occupationOpts)}</p>
                            </div>
                            <Field label={"Expected Salary"} name="expectedSalary" value={form.expectedSalary} editing={false} />
                            <div>
                                <label style={{fontSize:13,fontWeight:700,color:'#374151'}}>Expected Status</label>
                                <p style={{marginTop:6,fontSize:15,color:'#374151'}}>{resolveMulti(form.expectedStatus, allStatus)}</p>
                            </div>
                            <div>
                                <label style={{fontSize:13,fontWeight:700,color:'#374151'}}>Expected Stars</label>
                                <p style={{marginTop:6,fontSize:15,color:'#374151'}}>{resolveMulti(form.expectedStars, allStars)}</p>
                            </div>
                            <div>
                                <label style={{fontSize:13,fontWeight:700,color:'#374151'}}>Expected Zodiac Signs</label>
                                <p style={{marginTop:6,fontSize:15,color:'#374151'}}>{resolveMulti(form.expectedZodiacSigns, zodiacSigns)}</p>
                            </div>
                            <Field label={"Expected Education"} name="expectedEducation" value={form.expectedEducation} editing={false} />
                        </div>
                    </div>

                    <hr style={{border:'none',borderTop:'1px solid rgba(224,207,160,0.3)'}}/>

                    {/* Section: About */}
                    <div>
                        <p style={{fontSize:13,fontWeight:800,color:'#C8920A',letterSpacing:'0.08em',textTransform:'uppercase',marginBottom:16,display:'flex',alignItems:'center',gap:8}}>💬 {t("aboutExpectations")}</p>
                        <Field label={t("expectations")} name="expectation" value={form.expectation} editing={false}
                            onChange={handleFormChange} placeholder={t("phExpectations")}
                            tip={t("expectationsTip")} filled={progress.vals.expectation} />
                    </div>

                    <hr style={{border:'none',borderTop:'1px solid rgba(224,207,160,0.3)'}}/>

                    {/* Section: Family */}
                    <div>
                        <p style={{fontSize:13,fontWeight:800,color:'#C8920A',letterSpacing:'0.08em',textTransform:'uppercase',marginBottom:16,display:'flex',alignItems:'center',gap:8}}>👨‍👩‍👧 Family Details</p>
                        <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4">
                            <Field label={"Father's Name"}
                                value={lang === 'ta'
                                    ? [tamilDisplay.fatherFirst || form.fatherFirstName, tamilDisplay.fatherSurname || form.fatherSurname].filter(Boolean).join(" ")
                                    : [form.fatherFirstName, form.fatherSurname].filter(Boolean).join(" ")}
                                editing={false} />
                            <Field label={"Mother's Name"}
                                value={lang === 'ta'
                                    ? [tamilDisplay.motherFirst || form.motherFirstName, tamilDisplay.motherSurname || form.motherSurname].filter(Boolean).join(" ")
                                    : [form.motherFirstName, form.motherSurname].filter(Boolean).join(" ")}
                                editing={false} />
                            <Field label={"Father's Native"}      value={form.fatherNative}     editing={false} />
                            <Field label={"Father's Occupation"}  value={form.fatherOccupation} editing={false} />
                            <Field label={"Is Father Alive"} value={form.isFatherAlive === true || form.isFatherAlive === "true" ? "Yes" : form.isFatherAlive === false || form.isFatherAlive === "false" ? "No" : ""} editing={false} />
                            <Field label={"Mother's Occupation"}  value={form.motherOccupation} editing={false} />
                            <Field label={"Is Mother Alive"} value={form.isMotherAlive === true || form.isMotherAlive === "true" ? "Yes" : form.isMotherAlive === false || form.isMotherAlive === "false" ? "No" : ""} editing={false} />
                            <Field label={"Mother's Native"}      value={form.motherNative}     editing={false} />
                            <Field label={"No. of Siblings"}
                                value={form.numberOfSibilings !== "" && form.numberOfSibilings != null ? String(form.numberOfSibilings) : ""}
                                editing={false} />
                            <Field label={"Married Male Siblings"}   value={form.marriedMaleSibilings != null && form.marriedMaleSibilings !== "" ? String(form.marriedMaleSibilings) : ""}   editing={false} />
                            <Field label={"Married Female Siblings"} value={form.marriedFemaleSibilings != null && form.marriedFemaleSibilings !== "" ? String(form.marriedFemaleSibilings) : ""} editing={false} />
                        </div>
                        {form.siblingsDetails && (
                            <div style={{marginTop:12}}>
                                <label style={{fontSize:13,fontWeight:700,color:'#374151'}}>Siblings Details</label>
                                <p style={{marginTop:6,fontSize:15,color:'#374151',lineHeight:1.6}}>{form.siblingsDetails}</p>
                            </div>
                        )}
                        {form.assetInfo && (
                            <div style={{marginTop:12}}>
                                <label style={{fontSize:13,fontWeight:700,color:'#374151'}}>Asset Information</label>
                                <p style={{marginTop:6,fontSize:15,color:'#374151',lineHeight:1.6}}>{form.assetInfo}</p>
                            </div>
                        )}
                    </div>

                    <hr style={{border:'none',borderTop:'1px solid rgba(224,207,160,0.3)'}}/>

                    {/* Section: Contact */}
                    <div>
                        <p style={{fontSize:13,fontWeight:800,color:'#C8920A',letterSpacing:'0.08em',textTransform:'uppercase',marginBottom:16,display:'flex',alignItems:'center',gap:8}}>📍 தொடர்புகொள்ள தகவல் (Contact)</p>
                        <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4">
                            <Field label={"Email"}           value={form.email}               editing={false} />
                            <Field label={"WhatsApp"}        value={form.whatsappNumber}      editing={false} />
                            <Field label={"Address Line 1"}  value={form.presentAddressLine1} editing={false} />
                            <Field label={"Address Line 2"}  value={form.presentAddressLine2} editing={false} />
                            <Field label={"Present City"}    value={form.presentCity}         editing={false} />
                            <Field label={"District"}        value={form.presentDistrict}     editing={false} />
                            <Field label={"Present State"}   value={form.presentState}        editing={false} />
                            <Field label={"PIN Code"}        value={form.presentPinCode}      editing={false} />
                        </div>
                    </div>

                    <hr style={{border:'none',borderTop:'1px solid rgba(224,207,160,0.3)'}}/>

                    {/* Section: Native Address */}
                    <div>
                        <p style={{fontSize:13,fontWeight:800,color:'#C8920A',letterSpacing:'0.08em',textTransform:'uppercase',marginBottom:16,display:'flex',alignItems:'center',gap:8}}>🏠 பூர்விக முகவரி (Native Address)</p>
                        <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4">
                            <Field label={"Address Line 1"}  value={form.nativeAddressLine1} editing={false} />
                            <Field label={"Address Line 2"}  value={form.nativeAddressLine2} editing={false} />
                            <Field label={"Native City"}     value={form.nativeCity}         editing={false} />
                            <Field label={"Native District"} value={form.nativeDistrict}     editing={false} />
                            <Field label={"Native State"}    value={form.nativeState}        editing={false} />
                            <Field label={"PIN Code"}        value={form.nativePinCode}      editing={false} />
                        </div>
                    </div>
                </div>
            )}

            {/* ── SHORTLIST TAB ── */}
            {activeTab === "shortlist" && (
                <div style={{borderRadius:20,border:'1.5px solid rgba(224,207,160,0.35)',background:'#fff',padding:20}}>
                    {slLoading && (
                        <div style={{display:'flex',alignItems:'center',gap:10,padding:'40px 0',justifyContent:'center',color:'#6B7280',fontSize:14}}>
                            <HiOutlineArrowPath style={{fontSize:20,color:'#C8920A',animation:'spin 1s linear infinite'}}/>
                            {t("loadingShortlist")}
                        </div>
                    )}
                    {!slLoading && shortlist.length === 0 && (
                        <div style={{padding:'48px 0',textAlign:'center'}}>
                            <div style={{fontSize:48,marginBottom:12}}>💛</div>
                            <p style={{fontSize:16,fontWeight:700,color:'#374151',marginBottom:6}}>{t("shortlistEmpty")}</p>
                            <p style={{fontSize:14,color:'#9CA3AF',marginBottom:20}}>{t("shortlistEmptyDesc")}</p>
                            <button type="button"
                                onClick={() => window.history.back()}
                                style={{display:'inline-flex',alignItems:'center',gap:8,background:'linear-gradient(135deg,#0A1628,#112240)',color:'#F5D264',border:'none',borderRadius:12,padding:'12px 24px',fontSize:15,fontWeight:800,cursor:'pointer'}}>
                                {t("navMatches") || "Browse Matches"}
                            </button>
                        </div>
                    )}
                    {!slLoading && shortlist.length > 0 && (
                        <div style={{display:'grid',gridTemplateColumns:'1fr',gap:12}} className="sl-grid">
                            <style>{`@media(min-width:640px){.sl-grid{grid-template-columns:repeat(2,1fr)}} @media(min-width:1024px){.sl-grid{grid-template-columns:repeat(3,1fr)}}`}</style>
                            {shortlist.map((p) => {
                                const photo = getPhoto(p)
                                const initials = (p.firstName?.[0] || "M").toUpperCase()
                                const pAge = ageFromDob(p.dateOfBirth)
                                const pStar = getStar(p)
                                const removing = slRemoving === p._id
                                return (
                                    <div key={p._id} style={{borderRadius:16,border:'1.5px solid rgba(224,207,160,0.3)',background:'#FFFDF5',padding:14,display:'flex',flexDirection:'column',gap:12}}>
                                        {/* Top: avatar + info */}
                                        <div style={{display:'flex',alignItems:'center',gap:12}}>
                                            {photo
                                                ? <img src={photo} alt={formatName(p)} style={{width:56,height:56,borderRadius:12,objectFit:'cover',flexShrink:0,border:'2px solid rgba(200,146,10,0.2)'}}/>
                                                : <div style={{width:56,height:56,borderRadius:12,background:'#FDF0C8',color:'#C8920A',display:'flex',alignItems:'center',justifyContent:'center',fontSize:20,fontWeight:900,flexShrink:0}}>{initials}</div>
                                            }
                                            <div style={{minWidth:0,flex:1}}>
                                                <p style={{fontSize:15,fontWeight:800,color:'#0A1628',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{formatName(p)}</p>
                                                <p style={{fontSize:13,color:'#6B7280',marginTop:2}}>
                                                    {[p.gender, pAge && `${pAge} ${t("yrs")}`, pStar].filter(Boolean).join(" · ") || "—"}
                                                </p>
                                                {(p.occupationDetail || p.occupation) && (
                                                    <p style={{fontSize:12,color:'#9CA3AF',marginTop:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{p.occupationDetail || p.occupation}</p>
                                                )}
                                            </div>
                                        </div>
                                        {/* Action buttons */}
                                        <div style={{display:'flex',gap:8}}>
                                            <button type="button"
                                                onClick={() => { window.location.href='/member' }}
                                                style={{flex:1,display:'flex',alignItems:'center',justifyContent:'center',gap:6,padding:'9px 8px',borderRadius:10,background:'linear-gradient(135deg,#0A1628,#112240)',color:'#F5D264',border:'none',fontSize:13,fontWeight:800,cursor:'pointer'}}>
                                                {t("viewProfile") || "View Profile"}
                                            </button>
                                            <button type="button"
                                                onClick={() => removeFromShortlist(p._id)}
                                                disabled={removing}
                                                style={{display:'flex',alignItems:'center',justifyContent:'center',padding:'9px 12px',borderRadius:10,border:'1.5px solid #FECACA',background:'#FEF2F2',color:'#DC2626',cursor:'pointer',fontSize:13,opacity:removing?0.5:1}}>
                                                {removing
                                                    ? <HiOutlineArrowPath style={{fontSize:14,animation:'spin 1s linear infinite'}}/>
                                                    : <HiOutlineTrash style={{fontSize:14}}/>}
                                            </button>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    )}
                </div>
            )}

            {/* ── Edit Profile Slide-Over ── */}
            <AnimatePresence onExitComplete={() => { document.body.style.overflow = "" }}>
                {editing && (
                    <motion.div
                        className="fixed inset-0 z-[80] flex justify-end bg-slate-950/45 backdrop-blur-sm"
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, pointerEvents: "none" }}
                        onClick={() => { setEditing(false); setProfileMsg(null) }}
                    >
                        <motion.aside
                            className="flex h-full w-full max-w-5xl flex-col overflow-hidden border-l border-borderColor bg-pColor shadow-2xl"
                            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
                            transition={{ type: "spring", damping: 30, stiffness: 260 }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Header */}
                            <div className="flex items-center justify-between gap-3 border-b border-borderColor bg-white px-5 py-4 shrink-0">
                                <div>
                                    <p className="text-xs font-bold uppercase tracking-[0.14em] text-primeColor">{t("myProfilePage")}</p>
                                    <h2 className="text-base font-semibold text-tColor">{t("editProfile")}</h2>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button type="button" onClick={() => { setEditing(false); setProfileMsg(null) }}
                                        title={t("cancel")}
                                        className="flex items-center gap-1.5 rounded-xl border border-borderColor bg-white px-4 py-2 text-sm font-bold text-slate-700 hover:border-slate-400 transition">
                                        <HiOutlineXMark className="h-4 w-4" />
                                        {t("cancel")}
                                    </button>
                                    <button type="button" onClick={saveProfile} disabled={saving}
                                        title={t("save")}
                                        style={{display:'flex',alignItems:'center',gap:6,borderRadius:12,background:'linear-gradient(135deg,#0A1628,#112240)',color:'#F5D264',padding:'10px 20px',fontSize:14,fontWeight:800,border:'none',cursor:'pointer',opacity:saving?0.6:1,minHeight:44}}>
                                        {saving
                                            ? <HiOutlineArrowPath className="h-4 w-4 animate-spin" />
                                            : <HiOutlineCheck className="h-4 w-4" />}
                                        {saving ? t("saving") : t("save")}
                                    </button>
                                </div>
                            </div>

                            {/* Body — scrollable */}
                            <div className="flex-1 overflow-y-auto p-6 space-y-6">
                                {profileMsg && <MsgBanner msg={profileMsg} />}

                                {/* Personal Details */}
                                <div style={{borderRadius:16,border:'1.5px solid rgba(224,207,160,0.3)',background:'#fff',padding:16,display:'flex',flexDirection:'column',gap:16}}>
                                    <p style={{fontSize:14,fontWeight:800,color:'#C8920A'}}>👤 {t("personalDetails")}</p>
                                    <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4">
                                        <Field label={t("firstName")}     name="firstName"                value={form.firstName}                editing={true} onChange={handleFormChange}
                                            tip={t("firstNameTip")} filled={progress.vals.firstName} />
                                        <Field label={t("lastName")}      name="lastName"                 value={form.lastName}                 editing={true} onChange={handleFormChange}
                                            tip={t("lastNameTip")} filled={progress.vals.lastName} />
                                        <Field label={t("dateOfBirth")}   name="dateOfBirth"              value={form.dateOfBirth}              editing={true} onChange={handleFormChange}
                                            type="date" tip={t("dateOfBirthTip")} filled={progress.vals.dateOfBirth} />
                                        <Field label={t("religion")}      name="religion"                 value={form.religion}                 editing={true} onChange={handleFormChange}
                                            filled={progress.vals.religion} />
                                        <Field label={t("caste")}         name="caste"                    value={form.caste}                    editing={true} onChange={handleFormChange}
                                            filled={progress.vals.caste} />
                                        <Field label={t("maritalStatus")} name="maritalStatus"            value={form.maritalStatus}            editing={true} onChange={handleFormChange}
                                            placeholder={t("phMarital")} filled={progress.vals.maritalStatus} options={MARITAL_OPTIONS} />
                                        <Field label={t("height")}        name="height"                   value={form.height}                   editing={true} onChange={handleFormChange}
                                            placeholder={t("phHeight")} filled={progress.vals.height} />
                                        <Field label={"Birth Time"}       name="birthTime"                value={form.birthTime}                editing={true} onChange={handleFormChange} placeholder="e.g. 06:30 AM" />
                                        <Field label={"Birth Place"}      name="birthPlace"               value={form.birthPlace}               editing={true} onChange={handleFormChange} placeholder="Town / City of birth" />
                                        <Field label={"No. of Children"}  name="noOfChild"                value={form.noOfChild}                editing={true} onChange={handleFormChange} type="number" />
                                        <Field label={"Child Details"}    name="childDetails"             value={form.childDetails}             editing={true} onChange={handleFormChange} placeholder="e.g. 1 boy, 1 girl" />
                                    </div>
                                    <div style={{display:'flex',flexDirection:'column',gap:8}}>
                                        <label style={{fontSize:13,fontWeight:700,color:'#374151'}}>About Me</label>
                                        <textarea name="aboutMe" value={form.aboutMe || ""} onChange={handleFormChange}
                                            placeholder="Write a brief about yourself…"
                                            rows={3}
                                            style={{...INPUT_STYLE,minHeight:'unset',height:'auto',resize:'vertical',lineHeight:1.6}}
                                            onFocus={e => Object.assign(e.target.style, FOCUS_STYLE)}
                                            onBlur={e  => Object.assign(e.target.style, BLUR_STYLE)}
                                        />
                                    </div>
                                </div>

                                {/* Education & Career */}
                                <div style={{borderRadius:16,border:'1.5px solid rgba(224,207,160,0.3)',background:'#fff',padding:16,display:'flex',flexDirection:'column',gap:16}}>
                                    <p style={{fontSize:14,fontWeight:800,color:'#C8920A'}}>🎓 {t("educationCareer")}</p>
                                    <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4">
                                        <Field label={t("education")}     name="educationalQualification" value={form.educationalQualification} editing={true} onChange={handleFormChange}
                                            placeholder={t("phEducation")} filled={progress.vals.educationalQualification} />
                                        <Field label={"Profession"}       name="profession"               value={form.profession}               editing={true} onChange={handleFormChange} placeholder="e.g. Software Engineer" />
                                        <Field label={t("occupation")}    name="occupationDetail"         value={form.occupationDetail}         editing={true} onChange={handleFormChange}
                                            placeholder={t("phOccupation")} filled={progress.vals.occupationDetail} />
                                        <Field label={"Occupation Location"} name="occupationLocation"    value={form.occupationLocation}       editing={true} onChange={handleFormChange} placeholder="City / Country" />
                                        <Field label={t("salary")}        name="salary"                   value={form.salary}                   editing={true} onChange={handleFormChange}
                                            placeholder={t("phSalary")} filled={progress.vals.salary} />
                                        <Field label={"Salary Mode"}      name="salaryMode"               value={form.salaryMode}               editing={true} onChange={handleFormChange}
                                            options={[{label:'Monthly',value:'Monthly'},{label:'Annual',value:'Annual'},{label:'Per Hour',value:'Per Hour'}]} />
                                        <Field label={t("nativePlace")}   name="native"                   value={form.native}                   editing={true} onChange={handleFormChange}
                                            placeholder={t("phNative")} filled={progress.vals.native} />
                                        <Field label={t("currentCity")}   name="city"                     value={form.city}                     editing={true} onChange={handleFormChange}
                                            placeholder={t("phCity")} filled={progress.vals.city} />
                                    </div>
                                </div>

                                {/* Astrology */}
                                <div style={{borderRadius:16,border:'1.5px solid rgba(224,207,160,0.3)',background:'#fff',padding:16,display:'flex',flexDirection:'column',gap:16}}>
                                    <p style={{fontSize:14,fontWeight:800,color:'#C8920A'}}>🔯 கூடுதல் தகவல் (Astrology)</p>
                                    <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4">
                                        <div style={{display:'flex',flexDirection:'column',gap:8}}>
                                            <label style={{fontSize:13,fontWeight:700,color:'#374151'}}>Zodiac Sign (ராசி)</label>
                                            <select name="zodiacSign" value={form.zodiacSign || ""} onChange={handleFormChange}
                                                style={SELECT_STYLE}
                                                onFocus={e => Object.assign(e.target.style, FOCUS_STYLE)}
                                                onBlur={e  => Object.assign(e.target.style, BLUR_STYLE)}>
                                                <option value="">— Select Zodiac Sign —</option>
                                                {zodiacSigns.map(z => <option key={z.value} value={z.value}>{z.text}</option>)}
                                            </select>
                                        </div>
                                        {/* Star — filtered immediately after Zodiac Sign */}
                                        <div style={{display:'flex',flexDirection:'column',gap:8}}>
                                            <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
                                                <label style={{fontSize:13,fontWeight:700,color:'#374151'}}>{t("starNakshatra")}</label>
                                                {form.zodiacSign && filteredStars.length > 0 && (
                                                    <span style={{fontSize:11,color:'#C8920A',fontWeight:600}}>({filteredStars.length})</span>
                                                )}
                                                {!progress.vals.starName && <MissingBadge />}
                                            </div>
                                            <select
                                                name="starName"
                                                value={form.starName || ""}
                                                onChange={handleFormChange}
                                                style={{...SELECT_STYLE, color: form.starName ? '#1F2937' : '#9CA3AF'}}
                                                onFocus={e => Object.assign(e.target.style, FOCUS_STYLE)}
                                                onBlur={e  => Object.assign(e.target.style, BLUR_STYLE)}
                                            >
                                                <option value="">— நட்சத்திரம் தேர்ந்தெடுக —</option>
                                                {/* When rasi selected: show ONLY that rasi's stars. No rasi: show all. */}
                                                {(form.zodiacSign
                                                    ? filteredStars
                                                    : (allStars.length > 0 ? allStars : NAKSHATRA_STATIC)
                                                ).map(s => (
                                                    <option key={s.value} value={s.text}>{s.text}</option>
                                                ))}
                                            </select>
                                            {form.zodiacSign && filteredStars.length === 0 && (
                                                <p style={{fontSize:11,color:'#C8920A',marginTop:-4}}>Loading stars…</p>
                                            )}
                                            {!form.zodiacSign && (
                                                <p style={{fontSize:11,color:'#9CA3AF',marginTop:-4}}>முதலில் ராசி தேர்ந்தெடுக்கவும்</p>
                                            )}
                                        </div>
                                        <div style={{display:'flex',flexDirection:'column',gap:8}}>
                                            <label style={{fontSize:13,fontWeight:700,color:'#374151'}}>Padham (பாதம்)</label>
                                            <select name="padham" value={form.padham || ""} onChange={handleFormChange}
                                                style={SELECT_STYLE}
                                                onFocus={e => Object.assign(e.target.style, FOCUS_STYLE)}
                                                onBlur={e  => Object.assign(e.target.style, BLUR_STYLE)}>
                                                <option value="">— Select Padham —</option>
                                                <option value="1">1</option>
                                                <option value="2">2</option>
                                                <option value="3">3</option>
                                                <option value="4">4</option>
                                            </select>
                                        </div>
                                        <div style={{display:'flex',flexDirection:'column',gap:8}}>
                                            <label style={{fontSize:13,fontWeight:700,color:'#374151'}}>Thisai Iruppu (திசை இருப்பு)</label>
                                            <select name="thisaiIruppu" value={form.thisaiIruppu || ""} onChange={handleFormChange}
                                                style={SELECT_STYLE}
                                                onFocus={e => Object.assign(e.target.style, FOCUS_STYLE)}
                                                onBlur={e  => Object.assign(e.target.style, BLUR_STYLE)}>
                                                <option value="">— Select —</option>
                                                {allDhisa.map(d => <option key={d.value} value={d.value}>{d.text}</option>)}
                                            </select>
                                        </div>
                                        <div className="grid gap-2 grid-cols-3">
                                            <Field label={"Date"}  name="thisaiIruppuDate"  value={form.thisaiIruppuDate}  editing={true} onChange={handleFormChange} placeholder="DD" />
                                            <Field label={"Month"} name="thisaiIruppuMonth" value={form.thisaiIruppuMonth} editing={true} onChange={handleFormChange} placeholder="MM" />
                                            <Field label={"Year"}  name="thisaiIruppuYear"  value={form.thisaiIruppuYear}  editing={true} onChange={handleFormChange} placeholder="YYYY" />
                                        </div>
                                    </div>
                                    <MultiCheckField label={"Flaws / Dhosam"} name="flaws" value={form.flaws || []} onChange={handleFormChange} options={allFlaws} />
                                </div>

                                {/* Partner Preference */}
                                <div style={{borderRadius:16,border:'1.5px solid rgba(224,207,160,0.3)',background:'#fff',padding:16,display:'flex',flexDirection:'column',gap:16}}>
                                    <p style={{fontSize:14,fontWeight:800,color:'#C8920A'}}>💑 Partner Preference</p>
                                    <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4">
                                        <Field label={"Expected Education"} name="expectedEducation" value={form.expectedEducation} editing={true} onChange={handleFormChange} placeholder="e.g. B.E / M.B.A" />
                                        <Field label={"Expected Salary"}    name="expectedSalary"    value={form.expectedSalary}    editing={true} onChange={handleFormChange} placeholder="e.g. 50000+" />
                                    </div>
                                    <MultiCheckField label={"Expected Occupation"} name="expectedOccupation" value={form.expectedOccupation || []} onChange={handleFormChange} options={occupationOpts} />
                                    <MultiCheckField label={"Expected Status"}     name="expectedStatus"     value={form.expectedStatus || []}     onChange={handleFormChange} options={allStatus} />
                                    <MultiCheckField label={"Expected Stars"}      name="expectedStars"      value={form.expectedStars || []}      onChange={handleFormChange} options={allStars} />
                                    <MultiCheckField label={"Expected Zodiac Signs"} name="expectedZodiacSigns" value={form.expectedZodiacSigns || []} onChange={handleFormChange} options={zodiacSigns} />
                                </div>

                                {/* About & Expectations */}
                                <div style={{borderRadius:16,border:'1.5px solid rgba(224,207,160,0.3)',background:'#fff',padding:16,display:'flex',flexDirection:'column',gap:16}}>
                                    <p style={{fontSize:14,fontWeight:800,color:'#C8920A'}}>💬 {t("aboutExpectations")}</p>
                                    <Field label={t("expectations")} name="expectation" value={form.expectation} editing={true}
                                        onChange={handleFormChange} placeholder={t("phExpectations")}
                                        tip={t("expectationsTip")} filled={progress.vals.expectation} />
                                </div>

                                {/* Family Information */}
                                <div style={{borderRadius:16,border:'1.5px solid rgba(224,207,160,0.3)',background:'#fff',padding:16,display:'flex',flexDirection:'column',gap:16}}>
                                    <p style={{fontSize:14,fontWeight:800,color:'#C8920A'}}>👨‍👩‍👧 குடும்ப தகவல் (Family Details)</p>
                                    <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4">
                                        <Field label={"Father First Name"} name="fatherFirstName" value={form.fatherFirstName} editing={true} onChange={handleFormChange} />
                                        <Field label={"Father Surname"}    name="fatherSurname"   value={form.fatherSurname}   editing={true} onChange={handleFormChange} />
                                        <Field label={"Father's Native"}   name="fatherNative"    value={form.fatherNative}    editing={true} onChange={handleFormChange} />
                                        <Field label={"Father's Occupation"} name="fatherOccupation" value={form.fatherOccupation} editing={true} onChange={handleFormChange} />
                                        <Field label={"Is Father Alive"}  name="isFatherAlive"   value={form.isFatherAlive != null && form.isFatherAlive !== "" ? String(form.isFatherAlive) : ""}   editing={true} onChange={handleFormChange}
                                            options={[{label:'Yes',value:'true'},{label:'No',value:'false'}]} />
                                        <Field label={"Mother First Name"}    name="motherFirstName" value={form.motherFirstName} editing={true} onChange={handleFormChange} />
                                        <Field label={"Mother Surname"}       name="motherSurname"   value={form.motherSurname}   editing={true} onChange={handleFormChange} />
                                        <Field label={"Mother's Occupation"}  name="motherOccupation" value={form.motherOccupation} editing={true} onChange={handleFormChange} />
                                        <Field label={"Is Mother Alive"}  name="isMotherAlive"   value={form.isMotherAlive != null && form.isMotherAlive !== "" ? String(form.isMotherAlive) : ""}   editing={true} onChange={handleFormChange}
                                            options={[{label:'Yes',value:'true'},{label:'No',value:'false'}]} />
                                        <Field label={"Mother's Native"}  name="motherNative"    value={form.motherNative}    editing={true} onChange={handleFormChange} />
                                        <Field label={"No. of Siblings"}    name="numberOfSibilings"       value={form.numberOfSibilings}       type="number" editing={true} onChange={handleFormChange} />
                                        <Field label={"Male Siblings"}      name="numberOfMaleSibilings"   value={form.numberOfMaleSibilings}   type="number" editing={true} onChange={handleFormChange} />
                                        <Field label={"Female Siblings"}    name="numberOfFemaleSibilings" value={form.numberOfFemaleSibilings} type="number" editing={true} onChange={handleFormChange} />
                                        <Field label={"Married Male Siblings"}   name="marriedMaleSibilings"   value={form.marriedMaleSibilings}   type="number" editing={true} onChange={handleFormChange} />
                                        <Field label={"Married Female Siblings"} name="marriedFemaleSibilings" value={form.marriedFemaleSibilings} type="number" editing={true} onChange={handleFormChange} />
                                    </div>
                                    <div style={{display:'flex',flexDirection:'column',gap:8}}>
                                        <label style={{fontSize:13,fontWeight:700,color:'#374151'}}>Siblings Details</label>
                                        <textarea name="siblingsDetails" value={form.siblingsDetails || ""} onChange={handleFormChange}
                                            placeholder="e.g. Elder brother - married, Younger sister - unmarried"
                                            rows={2} style={{...INPUT_STYLE,minHeight:'unset',height:'auto',resize:'vertical',lineHeight:1.6}}
                                            onFocus={e => Object.assign(e.target.style, FOCUS_STYLE)}
                                            onBlur={e  => Object.assign(e.target.style, BLUR_STYLE)} />
                                    </div>
                                    <div style={{display:'flex',flexDirection:'column',gap:8}}>
                                        <label style={{fontSize:13,fontWeight:700,color:'#374151'}}>Asset Information</label>
                                        <textarea name="assetInfo" value={form.assetInfo || ""} onChange={handleFormChange}
                                            placeholder="e.g. Own house, agricultural land…"
                                            rows={2} style={{...INPUT_STYLE,minHeight:'unset',height:'auto',resize:'vertical',lineHeight:1.6}}
                                            onFocus={e => Object.assign(e.target.style, FOCUS_STYLE)}
                                            onBlur={e  => Object.assign(e.target.style, BLUR_STYLE)} />
                                    </div>
                                </div>

                                {/* Contact & Present Address */}
                                <div style={{borderRadius:16,border:'1.5px solid rgba(224,207,160,0.3)',background:'#fff',padding:16,display:'flex',flexDirection:'column',gap:16}}>
                                    <p style={{fontSize:14,fontWeight:800,color:'#C8920A'}}>📍 தொடர்புகொள்ள தகவல் (Contact)</p>
                                    <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4">
                                        <Field label={"Email"}            name="email"               value={form.email}               editing={true} onChange={handleFormChange} type="email" placeholder="your@email.com" />
                                        <Field label={"WhatsApp Number"}  name="whatsappNumber"      value={form.whatsappNumber}      editing={true} onChange={handleFormChange} />
                                        <Field label={"Address Line 1"}   name="presentAddressLine1" value={form.presentAddressLine1} editing={true} onChange={handleFormChange} placeholder="Door No., Street" />
                                        <Field label={"Address Line 2"}   name="presentAddressLine2" value={form.presentAddressLine2} editing={true} onChange={handleFormChange} placeholder="Area / Locality" />
                                        <Field label={"Present City"}     name="presentCity"         value={form.presentCity}         editing={true} onChange={handleFormChange} />
                                        <Field label={"District"}         name="presentDistrict"     value={form.presentDistrict}     editing={true} onChange={handleFormChange} />
                                        <Field label={"Present State"}    name="presentState"        value={form.presentState}        editing={true} onChange={handleFormChange} />
                                        <Field label={"PIN Code"}         name="presentPinCode"      value={form.presentPinCode}      editing={true} onChange={handleFormChange} />
                                    </div>
                                </div>

                                {/* Native Address */}
                                <div style={{borderRadius:16,border:'1.5px solid rgba(224,207,160,0.3)',background:'#fff',padding:16,display:'flex',flexDirection:'column',gap:16}}>
                                    <p style={{fontSize:14,fontWeight:800,color:'#C8920A'}}>🏠 பூர்விக முகவரி (Native Address)</p>
                                    {/* "Same as present" checkbox */}
                                    <label style={{display:'flex',alignItems:'center',gap:10,padding:'10px 14px',borderRadius:12,border:'1.5px solid #E5E7EB',background:'#F9FAFB',cursor:'pointer',fontSize:14,fontWeight:600,color:'#374151'}}>
                                        <input
                                            type="checkbox"
                                            checked={sameAsPresent}
                                            onChange={(e) => {
                                                setSameAsPresent(e.target.checked)
                                                if (e.target.checked) {
                                                    setForm(prev => ({
                                                        ...prev,
                                                        nativeAddressLine1: prev.presentAddressLine1,
                                                        nativeAddressLine2: prev.presentAddressLine2,
                                                        nativeCity:         prev.presentCity,
                                                        nativeDistrict:     prev.presentDistrict,
                                                        nativeState:        prev.presentState,
                                                        nativePinCode:      prev.presentPinCode,
                                                    }))
                                                }
                                            }}
                                            style={{width:18,height:18,accentColor:'#C8920A',cursor:'pointer'}}
                                        />
                                        Use current address as native address
                                    </label>
                                    <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4">
                                        <Field label={"Address Line 1"}  name="nativeAddressLine1" value={form.nativeAddressLine1} editing={true} onChange={handleFormChange} placeholder="Door No., Street" />
                                        <Field label={"Address Line 2"}  name="nativeAddressLine2" value={form.nativeAddressLine2} editing={true} onChange={handleFormChange} placeholder="Area / Locality" />
                                        <Field label={"Native City"}     name="nativeCity"         value={form.nativeCity}         editing={true} onChange={handleFormChange} />
                                        <Field label={"Native District"} name="nativeDistrict"     value={form.nativeDistrict}     editing={true} onChange={handleFormChange} />
                                        <Field label={"Native State"}    name="nativeState"        value={form.nativeState}        editing={true} onChange={handleFormChange} />
                                        <Field label={"PIN Code"}        name="nativePinCode"      value={form.nativePinCode}      editing={true} onChange={handleFormChange} />
                                    </div>
                                </div>

                                {/* Jathagam Charts — last section */}
                                <div style={{borderRadius:16,border:'1.5px solid rgba(224,207,160,0.3)',background:'#fff',padding:16,display:'flex',flexDirection:'column',gap:16}}>
                                    <p style={{fontSize:14,fontWeight:800,color:'#C8920A'}}>🗂️ ஜாதகம் (Jathagam)</p>
                                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>
                                        <div>
                                            <p style={{fontSize:12,fontWeight:700,color:'#6B7280',marginBottom:8,textAlign:'center'}}>ராசி கட்டம் (Rasi)</p>
                                            <RasiKattam
                                                rasiValues={form.rasiChart}
                                                setRasiValues={(v) => setForm(prev => ({ ...prev, rasiChart: v }))}
                                                kattamInnerInjectclass="min-h-[56px] text-[11px]"
                                            />
                                        </div>
                                        <div>
                                            <p style={{fontSize:12,fontWeight:700,color:'#6B7280',marginBottom:8,textAlign:'center'}}>அம்சம் கட்டம் (Amsa)</p>
                                            <RasiKattam
                                                rasiValues={form.amsaChart}
                                                setRasiValues={(v) => setForm(prev => ({ ...prev, amsaChart: v }))}
                                                kattamInnerInjectclass="min-h-[56px] text-[11px]"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Save / Cancel at bottom — same layout as header */}
                                <div className="flex items-center justify-end gap-2">
                                    <button type="button" onClick={() => { setEditing(false); setProfileMsg(null) }}
                                        className="flex items-center gap-1.5 rounded-xl border border-borderColor bg-white px-4 py-2 text-sm font-bold text-slate-700 hover:border-slate-400 transition">
                                        <HiOutlineXMark className="h-4 w-4" />
                                        {t("cancel")}
                                    </button>
                                    <button type="button" onClick={saveProfile} disabled={saving}
                                        style={{display:'flex',alignItems:'center',gap:6,borderRadius:12,background:'linear-gradient(135deg,#0A1628,#112240)',color:'#F5D264',padding:'10px 20px',fontSize:14,fontWeight:800,border:'none',cursor:'pointer',opacity:saving?0.6:1,minHeight:44}}>
                                        {saving
                                            ? <HiOutlineArrowPath className="h-4 w-4 animate-spin" />
                                            : <HiOutlineCheck className="h-4 w-4" />}
                                        {saving ? t("saving") : t("save")}
                                    </button>
                                </div>
                            </div>
                        </motion.aside>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* ── Change Password Modal ── */}
            <AnimatePresence>
                {pwOpen && (
                    <motion.div
                        className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm px-4"
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        onClick={closePwModal}
                    >
                        <motion.div
                            className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl"
                            initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
                            onClick={e => e.stopPropagation()}
                        >
                            <div className="flex items-center justify-between mb-5">
                                <div>
                                    <p className="text-xs font-bold uppercase tracking-[0.14em] text-primeColor">{t("security")}</p>
                                    <h3 className="text-lg font-semibold text-tColor">{t("changePasswordTitle")}</h3>
                                </div>
                                <button type="button" onClick={closePwModal} title="Close"
                                    className="flex h-8 w-8 items-center justify-center rounded-full border border-borderColor text-slate-500 hover:border-primeColor hover:text-primeColor transition">
                                    <HiOutlineXMark className="h-4 w-4" />
                                </button>
                            </div>

                            <form onSubmit={changePassword} className="space-y-4">
                                <PwInput label={t("currentPassword")}    name="oldPassword"         value={pwForm.oldPassword}         onChange={handlePwChange} show={showPw.old}     onToggle={() => setShowPw(p => ({ ...p, old:     !p.old     }))} />
                                <PwInput label={t("newPassword")}         name="newPassword"         value={pwForm.newPassword}         onChange={handlePwChange} show={showPw.new}     onToggle={() => setShowPw(p => ({ ...p, new:     !p.new     }))} />
                                <PwInput label={t("confirmNewPassword")}  name="confirmNewPassword"  value={pwForm.confirmNewPassword}  onChange={handlePwChange} show={showPw.confirm} onToggle={() => setShowPw(p => ({ ...p, confirm: !p.confirm }))} />
                                <p className="text-xs text-slate-400">{t("passwordHint")}</p>
                                {pwMsg && <MsgBanner msg={pwMsg} />}
                                <button type="submit" disabled={pwSaving || !pwForm.oldPassword || !pwForm.newPassword}
                                    style={{width:'100%',display:'inline-flex',alignItems:'center',justifyContent:'center',gap:8,borderRadius:12,background:'linear-gradient(135deg,#0A1628,#112240)',color:'#F5D264',padding:'14px 24px',fontSize:15,fontWeight:800,border:'none',cursor:'pointer',opacity:(pwSaving||!pwForm.oldPassword||!pwForm.newPassword)?0.5:1,minHeight:52}}>
                                    {pwSaving ? <HiOutlineArrowPath className="h-4 w-4 animate-spin" /> : <HiOutlineLockClosed className="h-4 w-4" />}
                                    {pwSaving ? t("changing") : t("changePassword")}
                                </button>
                            </form>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* ── Payment Modal ── */}
            <PaymentModal
                open={!!payModal}
                plan={payModal}
                onClose={() => setPayModal(null)}
                onSuccess={handlePaymentSuccess}
            />

            {/* ── Photo Lightbox ── */}
            {lightboxIdx !== null && currentImages[lightboxIdx] && (
                <div
                    onClick={() => setLightboxIdx(null)}
                    style={{position:'fixed',inset:0,zIndex:9999,background:'rgba(0,0,0,0.88)',display:'flex',alignItems:'center',justifyContent:'center',padding:16}}
                >
                    {/* Close button */}
                    <button
                        onClick={() => setLightboxIdx(null)}
                        style={{position:'absolute',top:16,right:16,background:'rgba(255,255,255,0.15)',border:'none',borderRadius:99,width:40,height:40,display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',color:'#fff',fontSize:22,backdropFilter:'blur(4px)'}}
                    >✕</button>

                    {/* Prev arrow */}
                    {currentImages.length > 1 && (
                        <button
                            onClick={e => { e.stopPropagation(); setLightboxIdx((lightboxIdx - 1 + currentImages.length) % currentImages.length) }}
                            style={{position:'absolute',left:16,top:'50%',transform:'translateY(-50%)',background:'rgba(255,255,255,0.15)',border:'none',borderRadius:99,width:44,height:44,display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',color:'#fff',fontSize:24,backdropFilter:'blur(4px)'}}
                        >‹</button>
                    )}

                    {/* Photo */}
                    <img
                        src={currentImages[lightboxIdx].imageUrl}
                        alt={`Photo ${lightboxIdx + 1}`}
                        onClick={e => e.stopPropagation()}
                        style={{maxWidth:'90vw',maxHeight:'88vh',borderRadius:16,objectFit:'contain',boxShadow:'0 8px 48px rgba(0,0,0,0.6)'}}
                    />

                    {/* Next arrow */}
                    {currentImages.length > 1 && (
                        <button
                            onClick={e => { e.stopPropagation(); setLightboxIdx((lightboxIdx + 1) % currentImages.length) }}
                            style={{position:'absolute',right:16,top:'50%',transform:'translateY(-50%)',background:'rgba(255,255,255,0.15)',border:'none',borderRadius:99,width:44,height:44,display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',color:'#fff',fontSize:24,backdropFilter:'blur(4px)'}}
                        >›</button>
                    )}

                    {/* Dot indicators */}
                    {currentImages.length > 1 && (
                        <div style={{position:'absolute',bottom:20,left:'50%',transform:'translateX(-50%)',display:'flex',gap:8}}>
                            {currentImages.map((_, i) => (
                                <button
                                    key={i}
                                    onClick={e => { e.stopPropagation(); setLightboxIdx(i) }}
                                    style={{width: i === lightboxIdx ? 20 : 8,height:8,borderRadius:99,border:'none',cursor:'pointer',background: i === lightboxIdx ? '#F5D264' : 'rgba(255,255,255,0.4)',transition:'all 0.2s'}}
                                />
                            ))}
                        </div>
                    )}
                </div>
            )}

        </motion.div>
    )
}

export default MemberPortal
